/**
 * `gate` (§7.5, D120) — the state-first Gate Detail card. Ported from
 * `prototype/screens/gate-detail.js`. Owns the control card (Connection/Status tiles), the
 * Open/Pedestrian actuators sharing one access/refusal path, Personalize + Gate details rows,
 * manager-only Access management, and Remove me last.
 *
 * **The "Schedules" nav row is back**, now that P15's panel exists to receive it — gated by
 * the shabbat_panel remote flag (`config/`, R9/D329) so a stage without P8's API deployed
 * still shows nothing (implementation-notes.md → P7, superseding Deviation 2/15, dated entries).
 * **The read-only Shabbat status row above the Open button was removed again**: the gate-mode
 * segmented control's own "מתוזמן" segment (below, in Access management) already carries the same
 * on/off signal, so the standalone row was a second copy of one fact rather than a second fact.
 *
 * **R1/F9 correction (2026-08-13): D82's physical-remote sentence is back.** Deviation 15 removed
 * it alongside the Schedule tile/row on the reasoning that it was "spec commentary, not in-phone
 * copy a resident needs" — but §13 "Gate Detail follows D120's hierarchy in Hebrew and English"
 * says in as many words that "D82's physical-remote warning remains visible beside Permanent
 * close," and the parent PRD wins every disagreement with an implementation note. Nothing about
 * D119–D121's card-based hierarchy relocated the warning elsewhere; it was dropped outright, so
 * this restores the original sentence to its original place rather than adding a second copy of
 * it anywhere else.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { formatGateId, type DenyReason, type GateId, type GatePosition } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { beginOpenTiming, type OpenTiming, type OpenTimingVerdict } from '../shared/openTiming'
import { useSession } from '../../auth/context'
import { NetworkError, RequestTimeoutError } from '../../auth/transport'
import {
  AccessBlockLabel,
  AppBar,
  ButterPop,
  FrameOverlay,
  Icon,
  MaterialIcon,
  Popover,
  ToastHost,
  type IconName,
  personalizationColor,
  personalizationWash,
  isViewColor,
  renderNameForViewIcon,
} from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useDismissOnBack, useNavigation } from '../../router'
import {
  accessForDetail,
  iconForGateType,
  isSubscriptionExpired,
  renewalDaysRemaining,
  SUBSCRIPTION_LOW_DAYS,
} from '../shared/access'
import {
  closeGate,
  closeModeOf,
  deleteGate,
  getGate,
  getRoster,
  openGate,
  patchGateName,
  type GateDetail,
} from '../shared/api'
import { cachedGateDetail } from '../shared/cachedGate'
import { CountBadge, DetailRow } from '../shared/DetailRow'
import { putGateHandoff } from '../shared/gateHandoff'
import { takePendingGateToast } from '../shared/pendingToast'
import {
  bleFailureKey,
  REFUSAL_KEY,
  bleAdapter,
  evaluateFreshness,
  hasCredential,
  holdsCredential,
  openLocally,
  pickTransport,
  decideFallback,
  transportLineKey,
  warmCredential,
  type CloudVerdict,
  type RenderedLocalFailure,
  type TransportPlan,
} from '../../ble'
import { useDelayedIndicator } from '../../hooks/useDelayedIndicator'
import {
  closeFailureKey,
  closeFailurePatch,
  contendedFailurePatch,
  openFailureKey,
  isContendedOpenRefusal,
  deliveryFailurePatch,
  isNoDataFailure,
  type RenderedCloseFailure,
  type RenderedDeliveryFailure,
} from '../shared/openFailure'
import { openDeviceStore } from '../../device-cache'
import { STAGE } from '../../auth/config'
/* Home's overlay, on Home's terms — see `runCommand` below. The three-beat contract, the style
   switch and `prefersReducedMotion` all live beside the animation itself, so this screen imports
   the same components rather than growing a second copy of the choreography; P6's preferences
   screen already reaches across for the same one. */
import { OpenAnimation } from '../home/OpenAnimation'
import { prefersReducedMotion, type GateOpenOutcome } from '../home/GateOpenOverlay'
import { readOpenAnimation, type OpenAnimationStyle } from '../../theme/openAnimation'
import { DeleteGateDialog } from './DeleteGateDialog'
import { RemoveMePopover } from './RemoveMePopover'

/**
 * **R7/D281 — the four halves the card can render, as ONE union rather than a command plus a
 * direction flag.**
 *
 * `command.command` has always answered *which control is in flight*, and with the actuators
 * paired there are four of them. A second `direction` field beside a two-value `command` would
 * express the same four states in two places, and the pair `{command: 'normal', direction:
 * 'close'}` would be readable — which is a state the screen must never be in. One union makes it
 * unspellable, and `actionLabel`/`actionClass` fork on the member.
 *
 * Home reached the opposite shape for the opposite reason: its state is per **tile**, keyed by
 * `gateId`, and a tile has one control, so the direction there is a property of the command rather
 * than an identity of the button (`HomeScreen.tsx`'s `CommandDirection`).
 */
type CommandHalf = 'normal' | 'pedestrian' | 'close' | 'pedestrian-close'

/** The two halves that send a Close — the only ones `runClose` may set. */
function isCloseHalf(which: CommandHalf): boolean {
  return which === 'close' || which === 'pedestrian-close'
}

/**
 * `sending` is the request in flight; `sent` is the controller's acknowledgement — **not an open**.
 * What happens between `sent` and a gate that is actually open is the confirmation sequence below.
 */
type CommandState = {
  readonly command: CommandHalf | null
  readonly phase: 'idle' | 'sending' | 'sent' | 'failed'
  /** The position the gate reported in the acknowledging burst, before any confirm poll. */
  readonly ackPosition?: GatePosition
  readonly reason?: DenyReason
  readonly genericFailure?: boolean
  /**
   * **D272 F1 — the request never left the phone**, which is the one cause on this path a
   * resident can fix where they are standing. Home carries the identical field for the
   * identical reason; the two screens differ only in which namespace resolves the sentence.
   */
  readonly noData?: boolean
  /**
   * R1-7(d) — an access refusal `AccessBlockLabel` cannot render (its `reason` prop is core's
   * frozen `DenyReason`, not `screens/shared/api.ts`'s local `OpenRefusal`), so it gets its own
   * line beside `genericFailure`/`unconfirmed` below rather than folding into the generic message
   * a retry can never fix.
   *
   * **It kept its sentence when the two `409`s lost theirs** (R4/D223), and that is the rule
   * rather than an oversight: the binary governs *delivery*, and an access refusal is a third
   * thing a resident has to be able to act on.
   */
  readonly notAuthorized?: boolean
  /** The transport could not confirm delivery. **Not proof the gate did nothing** (PEC-8). */
  readonly unconfirmed?: boolean
  /**
   * **D294 — the open was refused because a CLOSE holds the gate**, which is a reason rather than a
   * delivery outcome and so cannot live on {@link deliveryFailure}. It selects the sentence through
   * `openFailureKey`; the slot, the `failed` phase and the line count are D272's.
   */
  readonly openRefusal?: 'gate-closing'
  /**
   * **D272/D274 — which of R6 §2's causes the transport reported, when it reported one.**
   * `undefined` beside `unconfirmed: true` is the honest *did not say* and renders exactly the
   * sentence this screen has always shown.
   */
  readonly deliveryFailure?: RenderedDeliveryFailure
  /**
   * **The transport actually used** (§6), not the one auto-pick preferred.
   *
   * Under D189 the picked *primary* and the used transport differ whenever a fallback ran: the
   * plan named cloud, cloud could not carry it, and BLE did. §6 requires the chip and the log row
   * to record what carried the command, so this is read from the attempt rather than the plan.
   */
  readonly usedTransport?: 'cloud' | 'ble'
  /** A local BLE failure. Each value is its own sentence — see `ble/localOpen.ts`. `'refused'`
   * is excluded by the type: R4/D223 routes it to `sent`, so it never reaches a sentence. */
  readonly bleFailure?: RenderedLocalFailure
  /**
   * **D318 — the cloud verdict this BLE leg was dispatched FOR, when one was.**
   *
   * The container dispatches the local leg with no tap on both verdicts (D240), so the out-of-range
   * sentence is the end of a *two-leg* attempt and must say which leg preceded it: after
   * `ambiguous` the cloud command may already have reached the gate. Absent is *no cloud leg ran*
   * and keeps `ble.localUnavailable` verbatim. Only `unavailable` reads it — `bleFailureKey` is the
   * whole of the branch.
   */
  readonly cloudVerdict?: CloudVerdict
  /**
   * **R7/D277 — a Failed close, and it is its own flag rather than `unconfirmed` re-used.**
   *
   * The two sentence sets are different (`openFailure.ts`: *the gate did not open* is not the
   * sentence a resident who asked for a close earns), and the two refusal maps invert two rows —
   * `gate-busy` is Success for an open and Failed for a close. One flag per direction is what
   * keeps the render from having to know which command produced the state it is drawing.
   */
  readonly closeFailed?: boolean
  /** Which of D274's causes the server named for a close, when it named one. */
  readonly closeFailure?: RenderedCloseFailure
  /** The two `409`s a close re-answers. `open-in-flight` is deliberately not one — it is Success. */
  readonly closeRefusal?: 'gate-busy' | 'close-not-supported'
}

const IDLE: CommandState = { command: null, phase: 'idle' }

/**
 * **D291 — a tap accepted while the gate read is still out.** An open carries the timing it was
 * started with, so the line it eventually settles measures the whole wait from the tap; a close is
 * not timed (see `runClose`).
 */
type QueuedTap =
  | {
      readonly kind: 'open'
      readonly pedestrian: boolean
      readonly command: 'normal' | 'pedestrian'
      readonly timing: OpenTiming
    }
  | { readonly kind: 'close'; readonly pedestrian: boolean; readonly half: CommandHalf }

/**
 * **How long the gate read may run before the held Open button explains itself (D272 F14).**
 *
 * 🔸 An author number with no distribution behind it, and it is deliberately **not**
 * `INDICATOR_DELAY_MS`. That constant is R5/D265's, chosen for the three list screens whose
 * skeletons it governs; this consumer waits on a *single gate read*, which is a different
 * distribution, and D272 inherited the 200 ms without re-reading it. At 200 ms an ordinary read —
 * around half a second — crossed the threshold and settled just after it, so the sentence appeared
 * and vanished on **every** entry to this screen: `measurements.md` §3.4 L10's own risk line,
 * *"too low and the flash it removes comes back"*, arriving on a fourth consumer it was not
 * written for.
 *
 * The sentence exists for the hanging network — up to `REQUEST_TIMEOUT_MS`, twenty seconds — so
 * any threshold well inside that still reports it; what a higher one buys is that the common case
 * says nothing at all, which is what D272 claimed the delay already did.
 *
 * `measurements.md` §3.4 L10 records the distribution as owed for this consumer specifically.
 */
export const HOLDING_FOR_ACCESS_DELAY_MS = 1200

/** How long a pending toast handed off from another screen (`pendingToast.ts`) stays up here. */
const PENDING_TOAST_HOLD_MS = 2500

/** R1-7(a) — `as const` so each value stays a literal, same as the Preferences rename's. */
const NAME_ERROR_KEYS = {
  required: 'editNameError',
  server: 'editNameErrorServer',
  network: 'editNameErrorNetwork',
} as const satisfies Record<'required' | 'server' | 'network', string>

/**
 * 🔸 When to look again after an acknowledgement, in milliseconds (D134).
 *
 * Just *past* each server-side confirm interval rather than on it: the reservation compares
 * `lastProbeAt < cutoff` strictly, so a poll landing exactly on the boundary is refused and wastes
 * the look. Three polls is the whole allowance `CONFIRM_PROBE_WINDOW_MS` buys.
 */
const CONFIRM_POLLS_MS: readonly number[] = [6_000, 12_000, 18_000]

/**
 * **D140 suspends the sequence above — the schedule is retained, not deleted.**
 *
 * With it off, nothing re-reads the gate after a command is sent, so this screen can no longer
 * reach *Gate is open*: `sent` is the terminal label and the amber in-flight styling is the
 * honest terminal state, because the acknowledgement is a delivery receipt and D135 forbids
 * claiming a physical state on the strength of one. The position that matters is handled at
 * `livePosition` below — a value held from *before* the command is last-known however it was
 * obtained, and must carry the qualifier that says so. Flip this back to `true` to restore
 * D134(b) whole; `confirmFor` stays deployed server-side with no caller meanwhile.
 */
const CONFIRM_POLLS_ENABLED = false

/**
 * 🔸 How long the delivered-command result stays on the button before it returns to *Open*.
 *
 * Under D134 the button had somewhere to go — *Sent* was a waypoint, and a probe moved it on. With
 * D140's sequence off there is no next state, so without this the control parks on *Sent* until the
 * screen is left: a gate-opening button that has stopped offering to open a gate. Ten seconds is
 * past a SEAV leaf's travel, so the resident has seen the outcome with their own eyes by the time
 * the label resets — and the button was never disabled in `sent`, so this changes what it *says*,
 * not what it can do.
 */
const COMMAND_RESULT_MS = 10_000

/** Positions at which there is nothing further to wait for. */
const SETTLED: ReadonlySet<GatePosition> = new Set<GatePosition>([
  'open',
  'stopped-while-opening',
  'stopped-while-closing',
])

type PositionKey =
  | 'positionOpen'
  | 'positionClosed'
  | 'positionOpening'
  | 'positionClosing'
  | 'positionStopped'
  | 'positionUnknown'

const POSITION_KEY: Readonly<Record<GatePosition, PositionKey>> = {
  closed: 'positionClosed',
  opening: 'positionOpening',
  open: 'positionOpen',
  closing: 'positionClosing',
  'stopped-while-opening': 'positionStopped',
  'stopped-while-closing': 'positionStopped',
}

export function GateDetailScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('gate')
  const { t: tCommon } = useTranslation('common')
  const { t: tAccess } = useTranslation('access')
  /**
   * B/D143 — the leave row's own explanation, reusing `RemoveMePopover`'s `blockedBody` copy
   * rather than inventing a second sentence that says the same thing. Read here (not lazily
   * inside the JSX below) so both call sites — the confirmed-sole paragraph and nothing else —
   * come from one namespace lookup.
   */
  const { t: tRemoveMe } = useTranslation('removeMe')
  /** P16's namespace — the transport line, the result chip, and the local failure copy. */
  const { t: tBle } = useTranslation('ble')
  /**
   * The install flow's namespace, for the two close-mode names alone (R7/D279). Read rather than
   * copied: `install.closeAuto`/`install.closeOnTap` are the strings the installer chose between,
   * and a `gate` copy of them is the divergence D269 refuses. Every catalog is statically bundled,
   * so a cross-namespace read costs a lookup and no loading.
   */
  const { t: tInstall } = useTranslation('install')
  // Both "stopped" variants read the same to a resident: the gate halted part-way. The
  // distinction is which direction it was travelling, which changes nothing they can do.
  const positionLabel = (position: GatePosition): string =>
    tAccess(POSITION_KEY[position] ?? 'positionUnknown')

  /**
   * **R5 step 4 — the screen paints from the snapshot and the read arrives afterwards.**
   *
   * `deviceCache.gates` is populated by the `GET /gates` that Home already made, so on the common
   * journey — Home → tile → detail — this is a hit and there is no loading state at all. The
   * `getGate` read still runs and still wins on arrival; it has stopped being the thing the screen
   * waits for.
   *
   * The seed is a lazy initializer rather than an effect, because an effect paints one blank frame
   * first, which is the whole defect. It is safe to read the cache during render: `deviceCache` is
   * context, not a subscription this component owns.
   */
  const [gate, setGate] = useState<GateDetail | undefined>(() => cachedGateDetail(deviceCache.gates, gateId))
  /**
   * **`true` only when there is nothing to paint** — a deep link into a gate this device has never
   * listed. Every other entry starts `false` and never shows `common.loading`.
   */
  const [loading, setLoading] = useState(() => cachedGateDetail(deviceCache.gates, gateId) === undefined)
  /**
   * **§4.3, amended by D291: paint everything, and hold the DISPATCH — not the button — until
   * `getGate` confirms.**
   *
   * A gate the server has revoked answers `403`/`404`, which leaves the screen (R1-7(b)) rather
   * than rendering a live Open button against a membership the server no longer recognizes. Under
   * a cache-first paint that refusal arrives *after* the first frame, so for one window a tap could
   * dispatch an open the server will refuse. R5/D265 answered that by disabling the actuators for
   * the window; **D291 keeps the reason and moves the hold**: a tap inside the window is ACCEPTED —
   * the button enters `sending`, the overlay rises — and is dispatched the instant the read
   * resolves, or discarded if the read says the gate is gone. No round trip is ever spent on a
   * revoked gate, which is the whole of what §4.3 refused, and the ~330 ms an ordinary read takes
   * (up to `REQUEST_TIMEOUT_MS` on a hanging network) is no longer a dead button the resident has
   * to notice and re-tap. The two rejected alternatives stand as recorded: dispatching live spends
   * that round trip, and gating on a cached `viewerStatus` is worse, because **the cache cannot
   * know about a server-side revocation**.
   *
   * It is not `loading`: `loading` is now false on the seeded path, which is exactly the path this
   * has to cover.
   */
  const [readConfirmed, setReadConfirmed] = useState(false)
  /**
   * **The dispatch decision reads THIS, never `readConfirmed` — the two settle on different
   * clocks, and a tap between them used to hang forever.** `load()`'s `finally` runs from a
   * promise continuation; the `setReadConfirmed(true)` it makes is committed by React on a later
   * Scheduler task, and the queue is released exactly once, synchronously, in that same `finally`.
   * A tap handled in the gap — a few ms on a mid phone, and the browser services input ahead of
   * React's task — ran the committed fiber's handler, read the state as still unsettled, queued
   * itself, and waited on a release that had already happened: `sending` forever, the overlay up,
   * no request sent, and D272's sentence never drawn because the state committed `true` a moment
   * later. The ref is written *before* the state and the release, so a handler that runs after the
   * read settled sees it settled, whatever React has painted. `readConfirmed` stays the render's
   * mirror — the held sentence reads it — and decides nothing. (Bugs-hunt row 13 F1.)
   */
  const readSettledRef = useRef(false)
  /**
   * **D291 — the one tap accepted while the read is still out.** One slot: a second tap while it
   * is held is refused by `command.phase === 'sending'`, exactly as a second tap during a dispatch
   * is. The ref is what `load()`'s `finally` reads — that function was created at mount and cannot
   * see this render's state — and the state mirror is what the held sentence renders from.
   */
  const queuedRef = useRef<QueuedTap | undefined>(undefined)
  const [queued, setQueued] = useState<QueuedTap | undefined>(undefined)
  /**
   * **D272 F14 — long enough that the held Open button is a question the resident is asking.**
   *
   * Since D291 the subject is a QUEUED tap rather than a disabled button: the sentence explains why
   * the tap the resident just made has not left the device yet. It is armed only while one is held,
   * so a resident who merely looks at the screen across a slow read sees nothing at all.
   *
   * Declared here rather than beside the sentence it feeds, because `gate === undefined` returns
   * early further down and a hook below that return is called conditionally — *"rendered more hooks
   * than during the previous render"*, which is a crash rather than a wrong pixel. See the
   * sentence's own comment for why it is delayed rather than immediate.
   */
  const holdingForAccess = useDelayedIndicator(
    !readConfirmed && queued !== undefined,
    HOLDING_FOR_ACCESS_DELAY_MS,
  )
  const [command, setCommand] = useState<CommandState>(IDLE)
  const [editingName, setEditingName] = useState<string | undefined>(undefined)
  /** R1-7(a) — typed the same way the Preferences screen's rename is: a distinct reason
   * on every failed branch, forty lines away, transplanted rather than reinvented. */
  const [nameError, setNameError] = useState<'required' | 'server' | 'network' | undefined>(undefined)
  const [nameBusy, setNameBusy] = useState(false)
  const [offline, setOffline] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteInFlight, setDeleteInFlight] = useState(false)
  const [deleteError, setDeleteError] = useState(false)
  /** D144 — "Remove me" is a popover on this screen, not a route of its own. */
  const [removingMe, setRemovingMe] = useState(false)
  /**
   * D143/D144/2026-08-14 — how many managers the roster **this screen's own read** reported.
   * `undefined` here means "this read hasn't answered" — see `knownManagerCount` below for the
   * value actually rendered, which also considers `gate.managerCount` (D153).
   *
   * `undefined` is "not known from this read", never `0`: a failed read must not claim a manager
   * count. Once this resolves it is the freshest evidence available and `knownManagerCount`
   * prefers it over `gate.managerCount` unconditionally.
   */
  const [managerCount, setManagerCount] = useState<number | undefined>(undefined)

  /**
   * Manager-only, shaped like `RemoveMePopover.tsx`'s `loadManagerCount()`: the bare `getRoster`
   * await must be caught, or an offline install reaches the exact R1-6 defect class this codebase
   * has already paid for once. Deliberately does **not** call `markRosterSeen()` — that write is
   * the People screen's (D89), and firing it from here would clear the unseen badge without the
   * manager ever having opened the roster.
   *
   * **The count it feeds is D143's, and it is the reason this read stays on this screen** after
   * D265 moved the access-list row that used to carry the other one. `soleManager` decides whether
   * the *Remove me* row exists at all, and that row is here.
   *
   * **R5 step 5 — and it deposits the whole roster on the way past.** The Access page's count badge
   * and the People screen's list both want exactly this response, and both used to re-issue it. The
   * deposit is the response itself rather than a derived number, because People needs the rows;
   * `gateHandoff.ts`'s slots are read-once, so a screen that never opens simply leaves it to be
   * replaced by the next visit's.
   */
  useEffect(() => {
    if (gate === undefined || gate.viewer.role !== 'manager') {
      setManagerCount(undefined)
      return
    }
    async function loadManagerCount(): Promise<void> {
      try {
        const result = await getRoster(session.request, gateId)
        setManagerCount(
          result.ok ? result.data.members.filter((member) => member.role === 'manager').length : undefined,
        )
        if (result.ok) putGateHandoff(gateId, { roster: result.data })
      } catch (error) {
        setManagerCount(undefined)
        if (!(error instanceof NetworkError)) throw error
      }
    }
    void loadManagerCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate?.viewer.role, gateId])

  /**
   * D145 — Android's hardware back closes these instead of leaving the screen.
   *
   * The delete dialog passes `dismissible: !deleteInFlight`, mirroring the backdrop it already
   * refuses while the `DELETE` is in flight. The two guards have to agree: a back button that
   * dismissed what the backdrop would not would leave a cancelled-looking screen over a
   * destructive request that is still running, which is the one outcome this dialog exists to
   * make impossible.
   */
  useDismissOnBack('gate:edit-name', editingName !== undefined, () => setEditingName(undefined))
  useDismissOnBack('gate:delete', deleting, () => setDeleting(false), {
    dismissible: !deleteInFlight,
  })
  /**
   * D144's Remove-me popover, registered here rather than inside `RemoveMePopover` because this
   * screen owns the state that decides whether it is mounted at all.
   *
   * It is left **dismissible unconditionally**, which is the opposite of the delete dialog above
   * and is deliberate: `RemoveMePopover` does its own roster read, and in both of its severe
   * states — D143's sole-manager alert, and the unanswered/failed roster — it withholds the
   * confirm entirely, so there is nothing in flight for a back tap to strand. The one write it
   * does make ends by resetting to Home, which unwinds this entry through `commit()`.
   */
  useDismissOnBack('gate:remove-me', removingMe, () => setRemovingMe(false))

  async function load(): Promise<void> {
    const fallBackToCache = (): void => {
      const cached = cachedGateDetail(deviceCache.gates, gateId)
      if (cached === undefined) return
      setGate(cached)
      setOffline(true)
    }

    // D291 — whether the read said this viewer no longer has the gate, so a queued tap is
    // discarded rather than dispatched at a server that has already refused it.
    let gone = false
    // R1-6: `setLoading(false)` used to sit after this block, so a rethrown non-`NetworkError`
    // skipped it and the screen stayed on "טוען…" forever. `finally` is what makes it
    // unconditional again, whichever branch below runs or throws.
    try {
      const result = await getGate(session.request, gateId)
      if (result.ok) {
        setGate(result.data)
        setOffline(false)
      } else if (result.status === 403 || result.status === 404) {
        /**
         * R1-7(b) — a revoked/removed gate must not render as "offline" with a live Open
         * button. `fallBackToCache()` below is for reachability failures only, where the cached
         * gate is still this viewer's gate and merely unreachable right now; a `403`/`404` means
         * the server itself no longer recognizes the membership, so the honest move is D144's
         * "every read 404s now" pattern — leave the screen — not a stale render of a control
         * this viewer can no longer use.
         */
        gone = true
        navigate.resetTo({ id: 'home' })
        return
      } else if (result.status !== 401) {
        // Only a `401` ends a session; every other failure (a 5xx) is offline-read-only (D129).
        fallBackToCache()
      }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      fallBackToCache()
    } finally {
      setLoading(false)
      /*
       * **Set in the `finally`, so a failed read releases the actuators too.** R5 step 4 holds
       * them for the one window in which a revocation could still arrive; once the read has
       * answered — with a gate, with a 5xx, or with nothing at all — that window is over, and a
       * resident offline at the gate must still be able to open it over BLE. The `403`/`404`
       * branch reaches this too — a `return` inside a `try` still runs its `finally` — and it does
       * not matter: that branch has already called `resetTo`, so the screen is leaving.
       *
       * **D291 — and the tap that was waiting on it goes out now, or goes nowhere.** Through the
       * ref, so the dispatch reads the plan and the access decision of the render that will paint
       * it (PEC-13 locks the transport at dispatch, never at the tap that queued it). A `403`/`404`
       * discards: the screen is leaving and the server has already said no.
       *
       * **The ref first.** From here on a tap dispatches directly; the state below is what the
       * next paint reads, and it lands later than any tap made in between (see `readSettledRef`).
       */
      readSettledRef.current = true
      setReadConfirmed(true)
      if (gone) discardQueuedRef.current()
      else dispatchQueuedRef.current()
    }
  }

  // The confirm sequence outlives individual renders but must not outlive the screen: a timer
  // firing after unmount would probe a gate nobody is looking at and spend its allowance.
  const confirmTimers = useRef<number[]>([])
  const clearConfirm = (): void => {
    for (const timer of confirmTimers.current) window.clearTimeout(timer)
    confirmTimers.current = []
  }

  /**
   * The delivered-command result's own timer (D140). Separate from the confirm timers because it
   * survives their suspension: those are cancelled by a flag, this one is the only thing that ever
   * ends a `sent` state now.
   */
  const resultTimer = useRef<number | undefined>(undefined)
  const clearResult = (): void => {
    if (resultTimer.current !== undefined) window.clearTimeout(resultTimer.current)
    resultTimer.current = undefined
    setFallbackOffer(undefined)
  }

  /**
   * **The offer to try the gate locally after cloud could not carry the command** — D189(b).
   *
   * **A control, not a transport selector.** It exists only after a failure, it names the one
   * command that failed, and it disappears with that failure's own result state. §6's *"never a
   * choice the user makes"* governs the happy path — which transport carries an ordinary tap —
   * and this is not that; it is the resident answering the one question PEC-13 says the client may
   * not answer for them.
   *
   * `verdict` is carried rather than recomputed because the copy above the button depends on it:
   * an ambiguous failure gets PEC-8's warning that the gate may already have the command, and a
   * never-dispatched one does not, since there is nothing to warn about.
   */
  const [fallbackOffer, setFallbackOffer] = useState<
    | {
        readonly command: 'normal' | 'pedestrian'
        readonly pedestrian: boolean
        readonly verdict: CloudVerdict
      }
    | undefined
  >(undefined)

  /**
   * **A command was sent while this screen was open, so any position it holds predates it.**
   *
   * Deliberately *not* derived from `command.phase`: the result now reverts to idle on its own,
   * and a position hidden for being stale must not reappear because the label reset. Nothing on
   * this screen probes after a command while D140 holds, so this is set once and stays set —
   * and **D296 removed the mount probe**, so nothing on this screen fetches one either: this is
   * set once and stays set for the life of the mount, and re-entering the screen no longer
   * changes that. It is inert while D140 holds — see `unverifiedSinceCommand` below.
   */
  const [commandSent, setCommandSent] = useState(false)

  /**
   * **The gate-opening overlay, and there is at most one** — the same layer Home raises, on the
   * screen whose two actuators are the other half of §7.5's open. It was Home-only, which meant
   * the identical tap reported itself two different ways depending on which screen it was made
   * from; the animation is the resident's answer to *did anything happen*, and Gate Detail is
   * where a resident goes when they are unsure.
   *
   * `style` is read once, when the overlay is raised, and held for the whole of that open: a
   * resident who changes the pill mid-flight must not have the choreography swapped underneath
   * them.
   */
  const [overlay, setOverlay] = useState<
    { readonly outcome: GateOpenOutcome; readonly style: OpenAnimationStyle } | undefined
  >(undefined)

  /**
   * Answers the standing overlay, and only while it is still `pending` — a late answer from an
   * attempt whose overlay has already left is a no-op rather than a second animation, and the
   * accepted-offer path (which raises none, exactly as Home's does not) reaches this harmlessly.
   * `...prev` rather than a fresh object so the style chosen at the raise survives the answer.
   */
  function answerOverlay(outcome: GateOpenOutcome): void {
    setOverlay((prev) => (prev !== undefined && prev.outcome === 'pending' ? { ...prev, outcome } : prev))
  }

  // Read through a ref so a timer scheduled several renders ago still calls the current cache.
  const deviceCacheRef = useRef(deviceCache)
  deviceCacheRef.current = deviceCache
  /**
   * **D291 — `load()`'s way to the CURRENT render's dispatch.** Both are re-assigned every render
   * (below, once `runCommand`/`runClose` exist), so the `finally` that releases the read always
   * calls a closure holding this render's `plan`, `access` and `gate` rather than the mount's.
   */
  const dispatchQueuedRef = useRef<() => void>(() => {})
  const discardQueuedRef = useRef<() => void>(() => {})

  /**
   * **D296 — `load()` only. This screen probes nothing on mount.**
   *
   * The probe that used to sit on the next line had exactly one live product, D243's wifi glyph,
   * and D243 had already taken that glyph's red — so it rendered *verified reachable this launch*
   * or nothing, and `checking`, `stale` and `offline` were one unmarked state. What it cost was
   * D125's lease on the gate root on **every** view of this screen, which a resident's own tap
   * then waits out. `livePosition` is not the counter-argument it reads as: it has been dead
   * end-to-end since D140 (`aab506e`), which is what D296 turns on.
   */
  useEffect(() => {
    void load()
    return () => {
      clearConfirm()
      clearResult()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  /**
   * **Warm the BLE credential while the network still exists** — the thing that makes an offline
   * open possible at all.
   *
   * The credential is memory-only (D85) and fetched just-in-time, and its only production caller
   * used to be the open itself. That was circular: online, §6 picks BLE only when a credential is
   * *already* held, so the open that would fetch one never ran; offline, the fetch needs the
   * network that is gone. **Both branches were unreachable**, and the symptom was a banner
   * promising Bluetooth followed by a refusal naming the gate — which the radio had never
   * addressed. `ble/credentials.ts`'s `warmCredential` carries the full account.
   *
   * **On the screen rather than at launch**, because a launch-time sweep over every gate would
   * pull every password the resident holds into memory to serve one tap. What it no longer has to
   * work around is the backgrounding: **D189(d) removed `discardOnHide`**, so a credential warmed
   * here survives the walk to the gate rather than being cleared the moment the phone goes in a
   * pocket. That is the D94 scenario, and until D189 this warm could not actually serve it —
   * the resident arrived with an empty map and no reception to refill it.
   *
   * **Above the loading and not-found returns, deliberately.** It reads `gate` optionally rather
   * than sitting beside the code that uses it, because a hook after an early return is a hook that
   * is sometimes not called — which React reports as *"rendered more hooks than during the previous
   * render"* the first time a gate finishes loading, and which is how this was written first.
   *
   * Guarded on `bleConfigured` and on support so no gate without a radio, and no browser without
   * one, spends a round trip. It never reports: a pre-fetch is not an event about the gate.
   */
  /*
   * **`session.request` is held in a ref rather than listed as a dependency, and that is what
   * keeps this a pre-fetch instead of a request loop.**
   *
   * `SessionManager.request()` calls `#set({ online: true })` after every non-401 response, and
   * `#set` allocates a new state object and notifies its listeners **whether or not a field
   * actually changed**. That re-renders `AuthProvider`, whose `sessionApi` is memoized on
   * `[manager, session]`, so **`session.request` has a new identity after every API call the app
   * makes**. Listed in the dependency array below, it re-armed this effect on the very response
   * the effect had just produced.
   *
   * The only thing that closed that circle was `!hasCredential(...)` going false — **which
   * happens on success and on nothing else**. So a *successful* warm stopped after one round trip
   * and a *failed* one never stopped at all: measured on a device against a stage where
   * `GET /gates/{id}/ble-access` is not deployed, a resident merely looking at Gate Detail issued
   * that request about twelve times a second, indefinitely. A 404 is only the cheapest way in —
   * a `403` on a revoked grant, a `429`, or any `5xx` closes the same loop.
   *
   * With the identity out of the deps the effect re-runs only when `warmable` itself changes,
   * which is the intended trigger. Under D189(d) the credential *survives* the backgrounding, so
   * the flip this waits on is the ordinary one — arriving at the screen without a warm copy, or
   * the server's five-minute reuse hint lapsing while the screen is open. A warm that *failed* is
   * deliberately not retried here; a pre-fetch is not an event about the gate, and the open
   * re-fetches on its own through `withCredential`.
   */
  const warmRequestRef = useRef(session.request)
  warmRequestRef.current = session.request
  const warmable =
    gate !== undefined &&
    deviceCache.online &&
    bleAdapter().supported() &&
    gate.bleConfigured !== false &&
    !hasCredential(gate.gateId)
  useEffect(() => {
    if (!warmable) return
    void warmCredential(warmRequestRef.current, gateId)
  }, [warmable, gateId])

  /**
   * The one-shot success notice a screen leaves behind before routing here (`pendingToast.ts`) —
   * the Shabbat panel's save confirmation, so far.
   *
   * **`takenToastRef` caches the read, not just a "did I already read" flag** — and that
   * distinction is what makes this survive `main.tsx`'s `StrictMode`, which double-invokes a
   * mount effect (run → cleanup → run again) before settling. A plain `useRef(false)` guard
   * (`bleVerifyStartedRef`'s shape) stops the *second* invocation from calling
   * `takePendingGateToast` again, which matters — it is one-shot and a second real call would
   * find nothing — but the *first* invocation's cleanup still fires as part of the same
   * double-invoke dance and removes whatever toast it just showed, and a guard that blocks the
   * second invocation from re-entering also blocks it from ever showing another. Caching
   * the resolved message (keyed to the `gateId` it was read for) lets the second, surviving
   * invocation stand its own toast up from the cached value with no second read.
   */
  const takenToastRef = useRef<{ readonly gateId: GateId; readonly message: string | undefined } | undefined>(
    undefined,
  )
  useEffect(() => {
    if (takenToastRef.current === undefined || takenToastRef.current.gateId !== gateId) {
      takenToastRef.current = { gateId, message: takePendingGateToast(gateId) }
    }
    const message = takenToastRef.current.message
    if (message === undefined) return
    const id = ButterPop.show({
      message,
      type: 'success',
      position: 'bottom-center',
      theme: 'default',
      duration: PENDING_TOAST_HOLD_MS,
      progress: true,
      closable: true,
      pauseOnHover: true,
      closeOnClick: true,
    })
    // Immediate, not animated — `ButterPop.remove()` rather than a dismissal. See `drop()` there:
    // under `StrictMode` this cleanup runs while the second mount's toast is already up.
    return () => ButterPop.remove(id)
  }, [gateId])

  if (loading) {
    return (
      <>
        <AppBar title={t('backTitle')} onBack={() => navigate.back()} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>{tCommon('loading')}</div>
      </>
    )
  }
  if (gate === undefined) {
    return (
      <>
        <AppBar title={t('backTitle')} onBack={() => navigate.back()} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>{t('notFound')}</div>
      </>
    )
  }

  const manager = gate.viewer.role === 'manager'
  /**
   * **D153: seeded from the projection/snapshot, corrected by the roster read — never the
   * reverse.** `gate.managerCount` reaches here from whichever source produced `gate`: a fresh
   * `GET /gates/{id}` (manager-only, contributed to P3's projection by
   * `managerCountContribution`) or `detailFromCache()`'s cached copy of the same field. That
   * value is present on the very first render a manager gate ever produces — including the
   * offline/cached one — which is what closes the flash the roster-read-only version of this
   * line used to have.
   *
   * `managerCount` (the roster-read state above) still wins once it resolves: it is strictly
   * fresher than whatever `gate` was carrying at mount, and `??` reaches for it first. Before it
   * resolves, the seed stands in. Both are `undefined`-safe by the same D46/D135 rule — an
   * absent or unresolved value is *unknown*, never `1`, so the row's default stays *present*
   * either way, and only a **confirmed** `1` from either source removes it.
   */
  const knownManagerCount = managerCount ?? gate.managerCount
  const soleManager = manager && knownManagerCount === 1
  /**
   * D103/D-A — whether the gate's own Shabbat automation is currently enabled. `scheduled`
   * reflects this rather than `operatingState`: the automation drives `operatingState` on its
   * own schedule, so it is what the segmented control's "מתוזמן" segment stands for.
   *
   * **Takes priority over `operatingState` for the segmented control's selection, exclusively —
   * a single-select control cannot show two segments selected at once.** A gate can be
   * `operatingState: 'normal'` (nothing has latched it yet this week) with the automation already
   * armed for the coming Shabbat; without this, "רגיל" and "מתוזמן" both read `selected` the
   * moment a schedule is saved.
   */
  const scheduleActive = gate.shabbatEnabled === true
  const normalSelected =
    !scheduleActive && (gate.operatingState === 'normal' || gate.operatingState === undefined)
  const permClosedSelected = !scheduleActive && gate.operatingState === 'perm-closed'
  const access = accessForDetail(gate)

  /*
   * ---------------------------------------------------------------------------------------
   * §6's auto-pick, now that there is a second transport to pick (P16).
   *
   * **Cloud is the primary and BLE backs it up** (D189(a)) — `pickTransport` holds that rule and
   * this screen holds none of it. What is computed here is only the four inputs, and three of them are
   * display inputs rather than authorization: the server evaluates `evaluateAccess({transport:
   * 'ble'})` on every credential fetch, and a cached grant never authorizes a command.
   *
   * `bleConfigured` is **manager-only on the projection**, so a resident sees `undefined` — which
   * is read as *unknown and therefore permitted*, exactly the fail-open-for-display rule D153's
   * `managerCount` and D143's `blocked` both use. Being wrong costs one refused round trip.
   *
   * `deviceCache.fetchedAt` is D61's clock: the instant this device last completed a successful
   * `GET /gates` under a live session. See `ble/freshness.ts` for why that is the conservative
   * reading of *"the last successful server sync"* and why conservative is the only safe
   * direction to be wrong in.
   */
  const freshness = evaluateFreshness(deviceCache.fetchedAt)
  const plan: TransportPlan = pickTransport({
    online: deviceCache.online,
    bleSupported: bleAdapter().supported(),
    blePermitted: gate.bleConfigured !== false,
    freshness,
    // R4/D217 — the **held** question, not the fresh one. See `PickInput.credentialHeld`; the
    // `offline: true` half below is what makes "no round trip" true of a lapsed copy.
    credentialHeld: holdsCredential(gate.gateId),
  })
  const transportKey = transportLineKey(plan, bleAdapter().supported())

  /**
   * **`readConfirmed` is deliberately NOT a term here since D291** — it holds the dispatch, not
   * the button. See its declaration above: a tap inside the read's window is queued and goes out
   * on the read's `finally`, so the control is live from the first paint and a revoked gate still
   * costs no round trip.
   *
   * **Offline is no longer unconditionally read-only for the actuator** — which is the whole of
   * what D94 said deferring this PRD gave up: *"a cellular outage at the gate means no resident
   * can open it at all."*
   *
   * Everything else on this screen stays read-only offline; only the open buttons change, and
   * only when auto-pick found a usable local path. `primary: 'none'` keeps them disabled and
   * the block below says **why**, in the words PEC-25/D84 fix.
   */
  const commandDisabled = access.reason !== null || command.phase === 'sending' || plan.primary === 'none'
  /**
   * **R7/D276 — whether this gate has a Close at all, resolved at the one helper.**
   *
   * `closeModeOf` is the single place the absent-reads-as-`auto` default lives (D279), so a
   * `gate.closeMode === 'command'` here would be the second copy of a default that eventually
   * disagrees with itself. A gate whose mode has changed under an open screen is repainted by the
   * next gate read; a tap in the window between is refused by the server with
   * `close-not-supported`, which `closeFailureKey` has a sentence for — the client is not the
   * safety layer (PEC-48).
   */
  const hasClose = closeModeOf(gate) === 'command'
  /**
   * **The close halves' own guard, and it differs from `commandDisabled` in exactly one term.**
   *
   * An access refusal and a latched gate (D168) bind identically — and D291's queue holds a Close
   * tapped inside the read's window exactly as it holds an Open — a
   * latched gate refuses a Close for the same reason it refuses an Open, and `accessForDetail`
   * puts the latch in `access.reason`. What is not shared is the transport: `plan.primary` is
   * auto-pick's answer for the OPEN path, and **there is no BLE close** (D277 (g)), so a device
   * with a warm credential and no reception has a usable local path for an open and none for a
   * close. The term is therefore the cloud's own reachability, which is what
   * `deviceCache.online` reports, and an offline resident meets a disabled Close rather than a
   * tap that ends in a twenty-second deadline.
   */
  const closeDisabled = access.reason !== null || command.phase === 'sending' || !deviceCache.online
  /**
   * **D296 — this screen makes no connectivity claim, and `cloudVerified` is gone with the probe
   * that was the only thing able to answer it.**
   *
   * D243 moved the LED's one honest claim onto the wifi glyph and took the red with it, so the
   * glyph said *verified reachable in this launch* or nothing and three of the four phases were
   * already indistinguishable. What D296 removes is the remaining half: with no mount probe,
   * `statusPhase` on this screen can never read `fresh`, so the green was a state nothing could
   * enter. The glyph itself stays, unmarked and unconditional — D189(a)'s pair is what stops a
   * lone bluetooth glyph reading as a transport that has *replaced* the cloud one, which is the
   * design that decision reversed, and that argument never depended on the green.
   *
   * `statusPhaseOf` and `deviceCache.statusPhase` are untouched and still answer all four phases:
   * the Install screen's check 1 is the surface where a red answer IS actionable (D243), and the
   * union describes evidence rather than this widget.
   */
  /**
   * Verified in this launch, or nothing. The acknowledging burst counts — it is a real answer
   * from the gate on this device, in this session — but the cached `lastPosition` does not.
   */
  const positionEvidence = deviceCache.livePosition(gate.gateId) ?? command.ackPosition
  /**
   * D140: with the confirm sequence off, the newest evidence this screen can hold once a command
   * has been sent predates that command — and since **D296** there is no mount probe either, so on
   * the ordinary path there is no reading at all; the acknowledging
   * burst is emitted while the leaf is still closed. Nothing looks again, so the position stops
   * being usable for the button's own label (`actionLabel` below) rather than becoming
   * last-known — §6's rule for exactly this case is that absence is honest and a guess is not.
   */
  const unverifiedSinceCommand = !CONFIRM_POLLS_ENABLED && commandSent
  // A trap for the next reader (and D140's restore inventory): `?? command.ackPosition` above
  // is therefore unreachable as a display value today too — `unverifiedSinceCommand` is true on
  // every beat that could carry a fresh `ackPosition` (a command was just sent), so this line
  // discards it before `livePosition` is ever read for one. It becomes live again only if
  // `CONFIRM_POLLS_ENABLED` is restored.
  const livePosition = unverifiedSinceCommand ? undefined : positionEvidence
  const cachedAlias = deviceCache.gates.find((g) => g.gateId === gate.gateId)
  const name = gate.alias ?? cachedAlias?.alias ?? gate.defaultName
  // Fix D: same offline/cache fallback as `name` above — `gate.icon`/`gate.color` are present on
  // `GateDetail` but were never read, so a personalized gate always fell back to the hardcoded
  // barrier/house default.
  const icon = gate.icon ?? cachedAlias?.icon
  const color = gate.color ?? cachedAlias?.color
  const expired = gate.subscription !== undefined && isSubscriptionExpired(gate.subscription.renewsAt)
  /**
   * B2 — Subscription row's colored days-remaining badge. `renewalDaysRemaining` already
   * returns `null` once the subscription has expired, which is what makes the badge **absent**
   * on an expired gate (the panel's own `expiredHint` above already says so — no second copy of
   * the same fact). The scale is `SUBSCRIPTION_LOW_DAYS`/double it — the same constant C3's
   * Home tile reads, so the two surfaces can never disagree about what "low" means. Distinct
   * from `renewalStrip()`'s longer Billing-screen sentence and its tighter 3/14-day cutoffs.
   */
  const subscriptionDays =
    gate.subscription === undefined ? null : renewalDaysRemaining(gate.subscription.renewsAt)
  const subscriptionBadgeVariant: 'green' | 'amber' | 'red' | undefined =
    subscriptionDays === null
      ? undefined
      : subscriptionDays < SUBSCRIPTION_LOW_DAYS
        ? 'red'
        : subscriptionDays < SUBSCRIPTION_LOW_DAYS * 2
          ? 'amber'
          : 'green'

  /**
   * Watch the gate move (D134).
   *
   * An acknowledgement arrives in ~300 ms and the leaf takes seconds, so the only way to say
   * anything true about the gate is to look again. Each poll presents `confirmFor`, which claims
   * the shorter confirm interval on the caller's own open; the answers land in the device cache
   * and the render reads them from there, so there is no state to keep in sync here and no stale
   * closure to get wrong.
   */
  function scheduleConfirm(idempotencyKey: string): void {
    clearConfirm()
    // D140. Guarded here rather than only at the call site so no future caller re-arms the
    // sequence by accident; the schedule below is unchanged and restored by the flag alone.
    if (!CONFIRM_POLLS_ENABLED) return
    for (const delay of CONFIRM_POLLS_MS) {
      confirmTimers.current.push(
        window.setTimeout(() => {
          // A settled gate has nothing left to report; stop rather than spend the allowance.
          const seen = deviceCacheRef.current.livePosition(gateId)
          if (seen !== undefined && SETTLED.has(seen)) return clearConfirm()
          void deviceCacheRef.current.probeGate(gateId, idempotencyKey)
        }, delay),
      )
    }
  }

  /**
   * **The local path — PEC-11's half of it, over `ble/localOpen.ts`.**
   *
   * The receipt rules moved to that module when D189(e) gave Home the same capability; what stays
   * here is the one rule that is about *this screen*: **show local success immediately**, from a
   * device that watched it happen, without waiting for a server the client cannot reach.
   */
  async function dispatchLocal(
    pedestrian: boolean,
    label: 'normal' | 'pedestrian',
    verdict?: CloudVerdict,
    /**
     * R6 A3 — the leg this dispatch belongs to. Absent for the offer buttons, which are the
     * resident's **own** second gesture and therefore their own tap, with their own `tapId`.
     */
    timing?: OpenTiming,
  ): Promise<void> {
    setFallbackOffer(undefined)
    /*
     * **The screen has to say something is happening, and until R4 it said the opposite.**
     *
     * Clearing the offer un-hides `openUnconfirmed` — that sentence is printed exactly when no
     * offer stands — and this path then awaited the whole exchange without touching `command`.
     * So from the moment a resident accepted the offer, through the chooser, the connect, the
     * 250 ms settle and up to a 4 s reply window, the screen showed **the previous attempt's
     * "the gate did not confirm"** with no sign that a second one was running. It read as a dead
     * button, and it is one of the three producers of the reported *"Bluetooth worked and still
     * said not confirmed"*.
     *
     * Home never had this: its offer button sets `beaming` before dispatching. This is the same
     * line, and the transport is stated from the start because this attempt is BLE by
     * construction — there is no fallback left to change it.
     */
    setCommand({ command: label, phase: 'sending', usedTransport: 'ble' })
    /*
     * **R4/D217 — a `never-dispatched` verdict says the network is down, and it knows better than
     * `deviceCache.online` does.**
     *
     * That flag is read from the render this dispatch closed over, so after a cloud attempt that
     * never left the handset it still says `true`. `openLocally` then passes `offline: false`,
     * D189(d)'s honour-the-held-copy branch is skipped, a lapsed reuse hint forces a re-fetch, and
     * the re-fetch has no network — so the resident is told there is no local access **while a
     * usable credential sits in memory**.
     *
     * `withCredential`'s own comment is the licence for this: *"`online: false` means the last
     * request failed, not the radio is off"*, and it **suppresses the expiry check, not the
     * fetch** — so nothing is lost when reception returns between the failure and the tap, which
     * is the case that comment was written for.
     *
     * `ambiguous` is untouched: there the request **did** reach the network, so the cache's own
     * reading is the honest one.
     */
    const reachedNetwork = verdict !== 'never-dispatched'
    /*
     * **The overlay is answered from a `finally`, which is Home's rule and not a tidiness one.**
     * Every exit from this dispatch is a verdict — including the one that leaves by throwing, and
     * including the automatic fallback `offerOrNothing` fires without awaiting — and an overlay
     * left `pending` is a wait animation running over a screen that has stopped reporting.
     */
    let outcome: GateOpenOutcome = 'refused'
    /*
     * **R6 A3 — defaulted to the throwing exit and delivered by the same `finally`**, on exactly
     * the argument stated above for the overlay: every exit is a verdict, and a leg that left no
     * line is a hole in the distribution indistinguishable from a tap nobody made.
     */
    let timingVerdict: OpenTimingVerdict = 'failed'
    let timingReason = 'threw'
    try {
      const result = await openLocally({
        request: session.request,
        gateId: gate!.gateId,
        command: pedestrian ? 'pedestrian' : 'open',
        online: deviceCache.online && reachedNetwork,
        userId: session.userId,
        stage: STAGE,
        openStore: openDeviceStore,
      })

      /*
       * **R4/D223 — `'refused'` settles into `sent` beside a clean open** (consuming D216's
       * verdict). A `CMD:Err` is the controller answering: it heard the command and declined it,
       * which is the same evidence the cloud path's `rejected` carries and which `toOpenResult()`
       * now routes to `outcome: 'opened'`. One tap must not answer differently depending on which
       * transport carried it.
       *
       * It takes `COMMAND_RESULT_MS` too, so the button returns to *Open* rather than parking on a
       * result — D140's argument for that timer does not change with the reason it was reached.
       *
       * **And the overlay answers from the same branch, which is the whole of what this merge
       * decided.** D229's wait is raised over a dispatch and answered by its verdict; D223 is the
       * decision about *which verdicts are Success*, so a `refused` that the button reports as
       * *sent* must not leave the overlay on its refusal beat. One condition, read once, feeds
       * both — a second copy of it here is how the button and the animation come to disagree.
       */
      if (result.ok || result.reason === 'refused') {
        outcome = 'opened'
        setCommand({ command: label, phase: 'sent', usedTransport: 'ble' })
        resultTimer.current = window.setTimeout(() => setCommand(IDLE), COMMAND_RESULT_MS)
        timingVerdict = 'opened'
        timingReason = result.ok ? 'delivered' : 'refused'
      } else {
        setCommand({
          command: label,
          phase: 'failed',
          usedTransport: 'ble',
          bleFailure: result.reason,
          // D318 — the leg this dispatch was made for. `exactOptionalPropertyTypes` is on, so an
          // explicit `undefined` does not assign and spreading `{}` is what says *no cloud leg ran*.
          ...(verdict === undefined ? {} : { cloudVerdict: verdict }),
        })
        // R6 A3 — a BLE `timeout` is PEC-8's `unconfirmed`, never a local refusal (§2 F11 vs F9).
        timingVerdict = result.reason === 'timeout' ? 'unconfirmed' : 'failed'
        timingReason = result.reason
      }
    } finally {
      answerOverlay(outcome)
      timing?.settle({ transport: 'ble', verdict: timingVerdict, reason: timingReason })
    }

    /*
     * **No `GET /gates` here, and the comment that used to justify one was false.** It read
     * *"a drain that finds nothing to do costs nothing"* — written in P16's own build commit,
     * by an author who believed `deviceCache.refresh()` drained the outbox. It fetched the gate
     * list. A BLE open never reaches the cloud, so no field of that response is about it, and the
     * drain that actually runs now is rule 5 inside `openLocally`.
     */
  }

  /**
   * **PEC-13, applied** — the rule itself is `decideFallback`, one copy shared with Home (D240),
   * because the same decision expressed on two screens is the divergence this seam had. What
   * stays here is the *rendering* of its three answers, which is this screen's alone.
   */
  /*
   * Returns **whether it dispatched**, which is what tells `runCommand` who owns the overlay: an
   * automatic local retry answers the wait itself when it lands (see `dispatchLocal`'s `finally`),
   * so reporting a refusal here would show a verdict the client is still trying to disprove. Home
   * makes the same handover through `settleFallback`'s returned outcome. A *standing offer* is not
   * a dispatch: nothing further is running, so the overlay is answered and leaves.
   */
  function offerOrNothing(
    label: 'normal' | 'pedestrian',
    pedestrian: boolean,
    verdict: CloudVerdict | null,
    tappedAt: number,
    /** R6 A3 — the tap this fallback belongs to, so its BLE leg is timed from the original tap. */
    timing: OpenTiming,
  ): boolean {
    /*
     * **D273's licence is `'withheld'` here, and its absence is the finding rather than an
     * omission.** D264 (h)'s diverted tap is Home's alone — this screen returns on
     * `plan.primary === 'ble'` and never makes a cloud attempt while latched — so there is no path
     * on which the cold refusal's premise could be false, and a screen that passed `'licensed'`
     * would be widening D240 (b) rather than applying D273.
     */
    const decision = decideFallback(plan, verdict, tappedAt, 'withheld')
    if (decision.act === 'nothing') return false
    if (decision.act === 'dispatch') {
      // `nextLeg()`, so this line's `elapsedMs` is the whole span the resident waited — the failed
      // cloud leg included. The radio's own cost is the difference against the cloud line.
      void dispatchLocal(pedestrian, label, decision.verdict, timing.nextLeg())
      return true
    }
    setFallbackOffer({ command: label, pedestrian, verdict: decision.verdict })
    return false
  }

  async function runCommand(pedestrian: boolean): Promise<void> {
    if (commandDisabled) return
    const command = pedestrian ? 'pedestrian' : 'normal'
    clearConfirm()
    clearResult()
    setCommandSent(true)
    setFallbackOffer(undefined)
    setCommand({ command, phase: 'sending' })

    /*
     * **The overlay is raised here and nowhere earlier.** `commandDisabled` has already refused a
     * blocked grant and a plan with no transport, so past this line a command genuinely leaves the
     * device — or, since D291, is held for the one window in which it may not — which is the only
     * thing the animation is entitled to draw a wait over. Under `prefers-reduced-motion` nothing
     * is mounted rather than reduced (D198): `global.css` zeroes every duration, so a mounted
     * overlay would arrive, swing and leave inside one frame.
     */
    if (overlay === undefined && !prefersReducedMotion()) {
      setOverlay({ outcome: 'pending', style: readOpenAnimation() })
    }
    /*
     * **R6 A3 — the tap is timed from here, past `commandDisabled`.** The overlay's own rule
     * (D229) is that the raise sits past that guard because a refusal before it sends nothing;
     * the measurement takes the same line, so a future change to one cannot silently move the
     * other. Since D291 a tap made before the gate read answers is timed from HERE too — the queue
     * is part of what the resident waited, and the line's `elapsedMs` says so.
     */
    const timing = beginOpenTiming({ surface: 'gate-detail', gateId: gate!.gateId })
    /*
     * **D291 — the read has not confirmed: hold the tap, not the button.** Everything above has
     * already happened — the button reads `sending`, the overlay is up — and what waits is the
     * dispatch alone, released by `load()`'s `finally` through `dispatchQueuedRef`. The transport is
     * NOT locked here: `dispatchOpen` reads the `plan` of the render that releases it (PEC-13 at
     * dispatch), because a read that fails on a dead network is exactly the case that changes it.
     * The ref and not the state, because the state can still read `false` after the one release
     * has run — a tap queued on that reading is never dispatched.
     */
    if (!readSettledRef.current) {
      queuedRef.current = { kind: 'open', pedestrian, command, timing }
      setQueued(queuedRef.current)
      return
    }
    await dispatchOpen(pedestrian, command, timing)
  }

  /**
   * **The open's dispatch, past every guard — D291 split it out of `runCommand` so a queued tap
   * can reach it from `load()`.** No guard of its own: the tap that reached it was already
   * admitted, and `command.phase === 'sending'` (set by that tap) would otherwise refuse it.
   */
  async function dispatchOpen(
    pedestrian: boolean,
    command: 'normal' | 'pedestrian',
    timing: OpenTiming,
  ): Promise<void> {
    /* The receipt this open ends on, defaulted to the refusal: every path out of the cloud attempt
       below is one except the branch that sets it otherwise. */
    let outcome: GateOpenOutcome = 'refused'

    /*
     * **PEC-13 — the transport is locked here, before dispatch, and for this attempt only.**
     * `plan` was computed at render; nothing re-evaluates it after this line. What D189(b) adds is
     * not an upgrade of a dispatched command but a **second attempt after a failed one**, and the
     * two are told apart by `CloudVerdict`: automatic only where the first never left the phone,
     * and otherwise offered to the resident with PEC-8's warning attached.
     */
    if (plan.primary === 'ble') {
      try {
        await dispatchLocal(pedestrian, command, undefined, timing)
      } catch (error) {
        setCommand({ command, phase: 'failed', genericFailure: true, usedTransport: 'ble' })
        if (!(error instanceof NetworkError)) throw error
      }
      return
    }

    const tappedAt = Date.now()
    let verdict: CloudVerdict | null = null
    try {
      const result = await openGate(session.request, gate!.gateId, pedestrian)
      // A `2xx` carrying no `outcome` is a malformed response, not an open (D135). Treating an
      // absent field as success is how a client reports a physical action it has no evidence for.
      if (result.ok && result.data.outcome === 'opened') {
        outcome = 'opened'
        setCommand({
          command,
          phase: 'sent',
          usedTransport: 'cloud',
          ...(result.data.position === undefined ? {} : { ackPosition: result.data.position }),
        })
        timing.settle({ transport: 'cloud', verdict: 'opened', reason: 'delivered' })
        if (result.data.idempotencyKey !== undefined) scheduleConfirm(result.data.idempotencyKey)
        // D140: the only thing that ends a `sent` state now. A failure is left standing — it
        // carries a reason the resident still needs, and clearing it would hide the refusal.
        resultTimer.current = window.setTimeout(() => setCommand(IDLE), COMMAND_RESULT_MS)
      } else if (result.ok && result.data.outcome === 'failed') {
        // The command was authorized and the transport did not confirm it. **Not proof the gate
        // did nothing** (PEC-8) — so the copy says so, nothing is retried automatically, and the
        // local path is *offered* rather than taken: the broker had it, and a silent second
        // dispatch is exactly PEC-13's forbidden case.
        setCommand({
          command,
          phase: 'failed',
          usedTransport: 'cloud',
          unconfirmed: true,
          ...deliveryFailurePatch(result.data.deliveryOutcome),
        })
        // R6 A3/§2 F2 — the wire carries `outcome: 'failed'` and nothing else, which is the
        // finding O7 exists to fix: five causes arrive here as one word.
        timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason: 'unconfirmed' })
        verdict = 'ambiguous'
      } else {
        /*
         * **R4/D223 — both `409`s are Success, and a Success offers nothing.**
         *
         * *"User get only success and failure. nothing in the middle."* Each names something the
         * platform is already carrying: D129's honest `gate-busy` is a neighbour's open holding
         * the gate lease, and D124's `open-in-flight` is this caller's own previous tap still
         * running. Both used to draw their own sentence, and `gate-busy` additionally set
         * `verdict = 'ambiguous'` — so the screen offered a **second dispatch against a command
         * that had already landed**, which is PEC-13's forbidden case reached sideways. Both
         * `verdict` assignments are therefore gone rather than nulled.
         *
         * They take `COMMAND_RESULT_MS` like any delivered command, so the button returns to
         * *Open* instead of parking on a result.
         *
         * `not-authorized` and every other reason are untouched: an access refusal still goes
         * through `AccessBlockLabel`, and an unrecognized failure is still the generic message.
         */
        const reason = result.reason
        if (reason === 'gate-busy' || reason === 'open-in-flight') {
          // The overlay follows the button, as on the BLE path above: D223 decides these two are
          // Success, so a wait raised over this dispatch must not leave on its refusal beat while
          // the control beside it reads *sent*.
          //
          // **D299 — neither earns a sentence, and `gate-busy`'s was withdrawn rather than
          // forgotten.** D297 gave it one because the code covered both directions; **D294 narrowed
          // it to an open holder**, so *sent* is true of it in direction as well as in existence and
          // the button's own label is the whole report.
          outcome = 'opened'
          setCommand({ command, phase: 'sent', usedTransport: 'cloud' })
          resultTimer.current = window.setTimeout(() => setCommand(IDLE), COMMAND_RESULT_MS)
          timing.settle({ transport: 'cloud', verdict: 'opened', reason })
        } else if (reason === 'gate-closing') {
          /*
           * **D294 — the gate is closing, so an Open is Failed and says which way it is travelling.**
           *
           * This is the matrix cell D223 got wrong and D297 could not reach: one `gate-busy` covered
           * both directions, so a tap made while the gate was shutting read Success and the gate kept
           * shutting. The slot, the flag and the `failed` phase are D272's; only the sentence is new,
           * and it is new because neither existing cause is true — the gate answered nothing (nothing
           * was asked of it) and it did not fail to move (it is moving, the other way).
           *
           * **`verdict` stays null, so no Bluetooth offer stands**, and here the reason is stronger
           * than D297's: the holder's close may still be travelling, so a local dispatch would be a
           * second command at a gate known to be in motion.
           */
          setCommand({ command, phase: 'failed', unconfirmed: true, openRefusal: 'gate-closing' })
          timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason })
        } else if (isContendedOpenRefusal(reason)) {
          /*
           * **D297 — the command holding the gate did not open it, and the resident is told which
           * way it failed.** The slot, the flag and the three sentences are D272's, reached by a
           * second door: no new failure string is minted, and `deliveryFailureKey`'s `undefined` row
           * answers `gate-command-failed`.
           *
           * **`verdict` stays null, so no Bluetooth offer stands** — D223's reason for that lapses
           * (these are Failed) and PEC-8's replaces it: the holder's command may have actuated
           * despite answering nothing, so a local dispatch would be a second command at a gate that
           * may already be moving. 🔸 Widening the fallback posture is its own decision.
           */
          setCommand({
            command,
            phase: 'failed',
            unconfirmed: true,
            ...contendedFailurePatch(reason),
          })
          timing.settle({ transport: 'cloud', verdict: 'unconfirmed', reason })
        } else if (reason === 'not-authorized') {
          setCommand({ command, phase: 'failed', usedTransport: 'cloud', notAuthorized: true })
          timing.settle({ transport: 'cloud', verdict: 'refused', reason })
        } else if (reason === undefined) {
          setCommand({ command, phase: 'failed', usedTransport: 'cloud', genericFailure: true })
          timing.settle({ transport: 'cloud', verdict: 'failed', reason: 'unknown' })
          verdict = 'ambiguous'
        } else {
          setCommand({ command, phase: 'failed', usedTransport: 'cloud', reason })
          timing.settle({ transport: 'cloud', verdict: 'refused', reason })
        }
        /*
         * **`not-authorized` and every `DenyReason` leave `verdict` null, and that is a rule
         * rather than an omission.** The server refused the *grant*; a local open is the same
         * command against the same refused grant, so offering Bluetooth there would be offering a
         * way around an access decision. It would also fail — `ble-access` runs the same
         * `evaluateAccess()` — but the reason not to offer it is the first one.
         */
      }
    } catch (error) {
      // R1-6: this used to run only when `error instanceof NetworkError`, and the rethrow for
      // every other error happened *before* it — leaving `phase: 'sending'` standing forever and
      // killing both actuator buttons for the life of the screen. Setting the state first and
      // deciding whether to rethrow after is what keeps the control operable either way.
      setCommand({
        command,
        phase: 'failed',
        genericFailure: true,
        // **D272 F1** — `isNoDataFailure`, never a bare `instanceof NetworkError`: two subclasses
        // inherit from it and neither is a phone with no data. That module carries the whole rule.
        ...(isNoDataFailure(error) ? { noData: true } : {}),
      })
      /*
       * **The one classification D189(b) turns on, and the subclass exists for it.**
       * A `fetch` that rejects in tens of milliseconds — no reception, DNS, CORS — never reached
       * the network. A `RequestTimeoutError` ran the full twenty-second budget, which means the
       * request *was sent* and may have reached the gate: PEC-8's ambiguity, not a clean miss.
       * Anything that is not a `NetworkError` at all is rethrown below, and is ambiguous on the
       * way out for the same reason.
       */
      verdict =
        error instanceof RequestTimeoutError
          ? 'ambiguous'
          : error instanceof NetworkError
            ? 'never-dispatched'
            : 'ambiguous'
      /*
       * **R6 A3 — this is the line R1 has been missing.** The unexplained ~8 s on a failed open
       * has two named candidates and both are here: a client deadline that ran its full
       * `REQUEST_TIMEOUT_MS` (§2 F6, which O8 would cut) and an automatic BLE leg queued behind
       * it, which `nextLeg()`'s own line then names. The reason reuses the classification the
       * screen already made for D189(b) rather than deriving a second one.
       */
      timing.settle({
        transport: 'cloud',
        verdict: 'failed',
        reason:
          error instanceof RequestTimeoutError
            ? 'client-timeout'
            : error instanceof NetworkError
              ? 'network'
              : 'threw',
      })
      if (!(error instanceof NetworkError)) {
        if (!offerOrNothing(command, pedestrian, verdict, tappedAt, timing)) answerOverlay(outcome)
        throw error
      }
    }

    if (!offerOrNothing(command, pedestrian, verdict, tappedAt, timing)) answerOverlay(outcome)
  }

  /**
   * **R7/D276 — the Close, and it is a SECOND function rather than a direction on `runCommand`.**
   *
   * Home's `runClose` made this call first and the argument is one layer up in `api.ts`: the two
   * routes invert two of D223's four rows, and a direction parameter is what lets a call site pass
   * the wrong one — here silently, on the command that moves a leaf toward a person. What the two
   * share is shared by call (`accessForDetail` through `closeDisabled`, `setCommand`,
   * `answerOverlay`, `OpenAnimation`); what is not shared is exactly what R7 inverted.
   *
   * Six things not to undo.
   *
   * **There is no BLE close (D277 (g)), so this never consults `plan` and never reaches the
   * radio.** A close is a cloud command or nothing, which is why `closeDisabled` reads
   * `deviceCache.online` where `commandDisabled` reads `plan.primary`. R7 §6's *do not let a Close
   * ride D240's automatic fallback* (D276 (e)) therefore holds **by construction**: nothing here
   * calls `offerOrNothing`, `decideFallback` computes no verdict for a direction with no second
   * transport, and there is no `CloudVerdict` on this path at all.
   *
   * **`gate-busy` is Failed and `open-in-flight` stays Success**, which is D277 (b)'s inversion and
   * its boundary in one place. D223 routes `409 gate-busy` to Success for an open because the lease
   * holder's command *is* the gate opening; for a close the holder is somebody **opening** the
   * gate, so the goal is the opposite of met and the sentence names the cause. `open-in-flight` is
   * this caller's own previous command still running, so the platform demonstrably has a command
   * for this gate — unchanged.
   *
   * **`rejected` is Failed for a close**, and that is measured rather than mirrored: R7 S1's C3
   * showed a bare Close on a moving leaf **stops** it, and C6 (3/3) that a Close onto a *closing*
   * leaf is ignored rather than refused, so D216's *already doing it* premise is false here. The
   * mapping lives in `openFailure.ts`'s `CLOSE_FAILURE_KEY`; this function reads it.
   *
   * **A refusal before sending raises no overlay**, exactly as `runCommand`'s does: the guard
   * returns above the raise, because a command that never left the device has no wait to draw
   * (D229). Past it the overlay carries **both** beats — D276 (g), vetoed at the owner on the day
   * of mint — the beat being a *delivery* receipt (D134) and never a claim the gate is shut.
   * 🔸 `gate`'s leaves still swing OPEN for a close; that is D276's own owed work on an existing
   * style rather than a new one (D215/D218 does not reach a direction).
   *
   * **It is not timed.** `beginOpenTiming` feeds R6 A3's open-latency distribution and a close
   * carries a different exchange (D277's compound: Stop, a settle, then the Close). Pooling the two
   * makes both unreadable — D239's own argument for the `Exchange` dimension, one transport down.
   *
   * **And `lastUsedAt` is not stamped**, which on this screen is a non-edit rather than a branch:
   * nothing here stamps it, and the reason it must stay that way is D163's — that line is *the
   * viewer's own most recent **open***, so a close moving it would date an opening that did not
   * happen.
   */
  async function runClose(pedestrian: boolean): Promise<void> {
    if (closeDisabled) return
    const half: CommandHalf = pedestrian ? 'pedestrian-close' : 'close'
    clearConfirm()
    clearResult()
    // D140's rule keys off *a command was sent*, not off a direction: the position on screen
    // predates this command whichever way it was travelling, so it stops being reportable here for
    // the same reason it does on an open.
    setCommandSent(true)
    setFallbackOffer(undefined)
    setCommand({ command: half, phase: 'sending' })

    if (overlay === undefined && !prefersReducedMotion()) {
      setOverlay({ outcome: 'pending', style: readOpenAnimation() })
    }

    // D291 — held exactly as an open is, on the ref and not the state; see `runCommand`.
    if (!readSettledRef.current) {
      queuedRef.current = { kind: 'close', pedestrian, half }
      setQueued(queuedRef.current)
      return
    }
    await dispatchClose(pedestrian, half)
  }

  /** The close's dispatch, past every guard (D291) — `dispatchOpen`'s twin, for the same reason. */
  async function dispatchClose(pedestrian: boolean, half: CommandHalf): Promise<void> {
    // The refusal is the default and `finally` delivers it, so the rethrow path below still
    // dismisses the overlay rather than leaving it running over a screen that has stopped
    // reporting — `runCommand`'s own structure, and R1-6's finding.
    let outcome: GateOpenOutcome = 'refused'
    try {
      const result = await closeGate(session.request, gate!.gateId, pedestrian)
      if (result.ok && result.data.outcome === 'opened') {
        // `toCloseResult` answers `'opened'` for `acknowledged` and `gate-acting` and nothing else,
        // so the Success set is the server's and this reads it rather than re-deriving it.
        outcome = 'opened'
        setCommand({ command: half, phase: 'sent', usedTransport: 'cloud' })
        resultTimer.current = window.setTimeout(() => setCommand(IDLE), COMMAND_RESULT_MS)
      } else if (result.ok && result.data.outcome === 'failed') {
        setCommand({
          command: half,
          phase: 'failed',
          usedTransport: 'cloud',
          closeFailed: true,
          ...closeFailurePatch(result.data.deliveryOutcome),
        })
      } else if (result.reason === 'open-in-flight' || result.reason === 'gate-closing') {
        /*
         * **D294 — `gate-closing` is this direction's Success, licensed by R7 S1's C6 (n=3): a Close
         * onto a *closing* leaf is ignored and the close completes**, so the holder's command meets
         * this caller's goal. It earns no sentence, the goal being met, and it shares
         * `open-in-flight`'s beat because both mean *the platform is closing this gate*. `gate-busy`
         * below is untouched and stays Failed — there the holder is **opening** (D277 (b)).
         */
        outcome = 'opened'
        setCommand({ command: half, phase: 'sent', usedTransport: 'cloud' })
        resultTimer.current = window.setTimeout(() => setCommand(IDLE), COMMAND_RESULT_MS)
      } else if (result.reason === 'gate-busy' || result.reason === 'close-not-supported') {
        setCommand({
          command: half,
          phase: 'failed',
          usedTransport: 'cloud',
          closeFailed: true,
          closeRefusal: result.reason,
        })
      } else if (result.reason === 'not-authorized') {
        // An access refusal is a third thing on this path too (D223), and it keeps its own line.
        setCommand({ command: half, phase: 'failed', usedTransport: 'cloud', notAuthorized: true })
      } else if (result.reason === undefined) {
        setCommand({ command: half, phase: 'failed', usedTransport: 'cloud', closeFailed: true })
      } else {
        // What remains is a `DenyReason` — the same `else` the open path's refusal chain rests on,
        // which is why `close-not-supported` earned its own union rather than widening
        // `OpenRefusal` (`tsc` refused the widening; see R7's S0-S4 block).
        setCommand({ command: half, phase: 'failed', usedTransport: 'cloud', reason: result.reason })
      }
    } catch (error) {
      setCommand(
        // **D272 F1, and the one open-named key a close may borrow.** `openNoData` reads *no
        // internet connection — the command wasn't sent*: it names the COMMAND rather than the
        // direction, in all five catalogs, so it is the honest sentence here, and a `closeNoData`
        // would be five strings that agree until one is retranslated (D255).
        isNoDataFailure(error)
          ? { command: half, phase: 'failed', genericFailure: true, noData: true }
          : { command: half, phase: 'failed', closeFailed: true },
      )
      if (!(error instanceof NetworkError)) throw error
    } finally {
      answerOverlay(outcome)
    }
  }

  /*
   * **D291 — this render's dispatch, handed to `load()` through the refs above.** Assigned every
   * render so the `finally` that releases the read runs the closure that holds the current `plan`
   * and `access`. A tap that would be refused by the time the read answers — the grant blocked in
   * between, a plan that lost its only transport on a failed read — ends on the refusal beat rather
   * than dispatching against a guard it no longer passes.
   */
  dispatchQueuedRef.current = (): void => {
    const held = queuedRef.current
    if (held === undefined) return
    queuedRef.current = undefined
    setQueued(undefined)
    const refused =
      access.reason !== null || (held.kind === 'open' ? plan.primary === 'none' : !deviceCache.online)
    if (refused) {
      setCommand(IDLE)
      answerOverlay('refused')
      if (held.kind === 'open')
        held.timing.settle({ transport: 'cloud', verdict: 'refused', reason: 'queued-refused' })
      return
    }
    if (held.kind === 'open') void dispatchOpen(held.pedestrian, held.command, held.timing)
    else void dispatchClose(held.pedestrian, held.half)
  }
  discardQueuedRef.current = (): void => {
    const held = queuedRef.current
    if (held === undefined) return
    queuedRef.current = undefined
    setQueued(undefined)
    setCommand(IDLE)
    setCommandSent(false)
    setOverlay(undefined)
    if (held.kind === 'open')
      held.timing.settle({ transport: 'cloud', verdict: 'refused', reason: 'gate-gone' })
  }

  /**
   * R1-7(a) — was `if (result.ok)` with no `else`: a failed rename left the popover open,
   * unchanged, and silent, with the confirm button still live for a request that had already
   * finished. Transplanted from the rename this app has had since D133 — a typed error on every branch
   * and `busy` cleared on every branch, including the one that rethrows.
   */
  async function confirmName(): Promise<void> {
    const value = (editingName ?? '').trim()
    if (value.length === 0) {
      setNameError('required')
      return
    }
    setNameBusy(true)
    setNameError(undefined)
    try {
      const result = await patchGateName(session.request, gate!.gateId, value)
      if (!result.ok) {
        setNameBusy(false)
        setNameError('server')
        return
      }
      setGate(result.data)
      setEditingName(undefined)
      setNameBusy(false)
    } catch (error) {
      setNameBusy(false)
      if (!(error instanceof NetworkError)) throw error
      setNameError('network')
    }
  }

  /**
   * The gate's own deletion (D124-D129). **`202` is the success**, not a `204`: the root has been
   * flipped to `deleting` and a stream worker finishes the cleanup, so there is nothing further to
   * wait for on this screen and Home is where the user belongs.
   *
   * `refresh()` is fired but deliberately not awaited before navigating. The gate is already gone
   * as far as this user is concerned, and blocking the exit on a list read means a slow or failed
   * refresh strands them on a screen for a gate they just deleted.
   */
  async function confirmDelete(): Promise<void> {
    if (deleteInFlight) return
    setDeleteInFlight(true)
    setDeleteError(false)
    try {
      const result = await deleteGate(session.request, gateId)
      if (!result.ok) {
        setDeleteError(true)
        setDeleteInFlight(false)
        return
      }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setDeleteError(true)
      setDeleteInFlight(false)
      return
    }
    void deviceCache.refresh()
    navigate.resetTo({ id: 'home' })
  }

  /**
   * The button's own state, and the whole point of D134: **`sent` is not `open`.**
   *
   * The confirmed label appears only when a probe answered in this launch says the gate is open —
   * never from the acknowledging burst, which is emitted while the leaf is still closed.
   *
   * D140 makes that branch **unreachable while the flag is off**, and deliberately leaves it
   * standing: `sent` becomes the terminal label. Do not "fix" the missing success state by
   * promoting the acknowledgement — that is the exact defect D134 was raised to remove.
   */
  function actionLabel(which: CommandHalf): string {
    /*
     * **D281 (b) — a verbal noun, and one string per DIRECTION rather than per half — and since
     * D283 that grammar belongs to the PAIRED card alone.** `פתיחה` and `סגירה` carry no
     * person and no gender, which is what lets one string sit under either heading; the heading
     * above the button is what says the subject. **An auto-close gate has no pair and no heading,
     * so it keeps the label that says its own subject** — `gate.openIdle` / `gate.pedestrianIdle`,
     * un-retired by D283 with `gate.pedestrianSub` beside them.
     *
     * `hasClose` is the fork, read from `closeModeOf` like every other one on this screen, so the
     * two grammars are never chosen by anything but the gate's own mode.
     */
    const idle = isCloseHalf(which)
      ? t('closeShort')
      : hasClose
        ? t('openShort')
        : t(which === 'normal' ? 'openIdle' : 'pedestrianIdle')
    if (command.command !== which || command.phase === 'idle' || command.phase === 'failed') return idle
    /*
     * **`שולח…` / `נשלח` are direction-neutral and are reused verbatim**, which is what makes the
     * in-flight and delivered states one vocabulary across the four halves. The pedestrian pair is
     * byte-identical to the gate pair in all five catalogs and is kept on the pedestrian halves so
     * neither key loses its last caller in a change that retires three others.
     */
    const pedestrianHalf = which === 'pedestrian' || which === 'pedestrian-close'
    if (command.phase === 'sending') return pedestrianHalf ? t('pedestrianSending') : t('openSending')
    /*
     * **R4/D223 — `gate.openConfirmed`/`gate.pedestrianConfirmed` retired, and `open` falls into
     * `positionLabel` with every other confirmed position.** The two keys were a second copy of
     * `access.positionOpen` in a namespace of their own, unreachable since D140 turned the
     * confirm sequence off, and D223's own non-goal is that *the resident is never told the gate
     * is open* on the strength of a delivery receipt. What is left is the position vocabulary,
     * which says the same thing only when a probe in this launch actually answered it — the
     * distinction D134 minted these labels to hold, now held by one set of strings instead of
     * two. **Restoring `CONFIRM_POLLS_ENABLED` still works**; the label it reaches is
     * `positionOpen` rather than `openConfirmed`.
     */
    /*
     * **The position vocabulary is the OPEN halves' alone.** It answers *the gate is open*, which
     * is what a close asked the gate to stop being, so a close reads its delivery receipt and
     * nothing else. D140 hides `livePosition` from the moment any command is sent anyway, so this
     * is unreachable in both directions today — stated rather than relied on, because restoring
     * `CONFIRM_POLLS_ENABLED` is what makes it reachable again.
     */
    if (!isCloseHalf(which) && livePosition !== undefined && livePosition !== 'closed') {
      return positionLabel(livePosition)
    }
    return pedestrianHalf ? t('pedestrianSent') : t('openSent')
  }

  /**
   * `opening` keeps its existing in-flight styling; `opened` is now reserved for a gate a probe
   * has actually confirmed open, rather than for a delivered command.
   *
   * Under D140 the button therefore rests in the `opening` treatment rather than turning green —
   * **which is why the colour matters here twice.** That treatment is **blue** since D228: it is
   * worn by `sending` *and* by a `sent` this screen will never confirm, and neither is a fault,
   * while amber is this app's warning hue. It is a static colour, not the `@keyframes pulse` that
   * implementation-notes.md → P7 entry 11 removed from this button, so a button resting in it does
   * not flicker. A standing local-fallback offer is a different statement and keeps amber
   * (`.offering`).
   */
  function actionClass(which: CommandHalf): string {
    if (command.command !== which) return ''
    if (command.phase === 'sending') return ' opening'
    // The confirmed beat is `livePosition === 'open'`, which is the gate being OPEN — the opposite
    // of what a close asked for. A close therefore rests in the in-flight treatment, exactly as an
    // open does under D140, and `global.css` declares no `.opened` for either close half.
    if (command.phase === 'sent') {
      return !isCloseHalf(which) && livePosition === 'open' ? ' opened' : ' opening'
    }
    return ''
  }

  const openLabel = actionLabel('normal')
  const pedestrianLabel = actionLabel('pedestrian')
  const closeLabel = actionLabel('close')
  const pedestrianCloseLabel = actionLabel('pedestrian-close')

  /**
   * **The offer this button is standing under, if it is standing under one** — D189(b) without a
   * second control.
   *
   * The extra *Open over Bluetooth* button is gone: the action that failed is the action that
   * retries, so the command's own button takes the offer's label and its dispatch for the offer's
   * life, and the pair can never disagree about which command a fallback would carry.
   * `fallbackOffer.command` is what keeps *Open* and *Pedestrian* from both claiming it.
   */
  function offerFor(which: 'normal' | 'pedestrian'): typeof fallbackOffer {
    return fallbackOffer !== undefined && fallbackOffer.command === which ? fallbackOffer : undefined
  }
  const openOffer = offerFor('normal')
  const pedestrianOffer = offerFor('pedestrian')
  /**
   * **A standing offer outranks `commandDisabled`, and only for the button that holds it.**
   * That flag also refuses on `plan.primary === 'none'`, which a device that has just gone
   * offline can reach *after* the cloud attempt failed — and refusing there would withdraw the
   * one path still open, which is the local one being offered. An in-flight command still wins.
   */
  const offerDisabled = command.phase === 'sending'

  return (
    <>
      {/* Fix E/F: once the title sits beside "חזרה"/"Back" instead of inside it, showing the
          back *destination* ("My gates") here reads wrong as a header — so the loaded bar
          shows the gate's own resolved name. The loading/notFound bars above keep `backTitle`
          because no name is known yet at that point. */}
      <AppBar title={name} onBack={() => navigate.back()} />
      <main className="gate-detail">
        <section className="gate-panel gate-control-card">
          {/*
            A11 (`ui-scale-refresh-handoff.md`) — the two-tile Connection/Status grid is gone,
            **D243 took the LED that replaced it**, and **D296 took the mount probe that fed the
            wifi glyph D243 moved the claim onto**. What is left on this row is identity.

            The chain is worth reading once, because each step removed a *reader* and the last one
            removed the write. B1 found that the connectivity phase and `deviceCache.livePosition()`
            came from the one `probeGate()` call the mount effect fired, so dropping the grid's text
            was a visual simplification rather than a cost tradeoff. `livePosition`'s own two
            consumers — the button's label (`actionLabel` below) and `positionLabel` — are
            unreachable while D140's `CONFIRM_POLLS_ENABLED` stays `false`: `actionLabel` returns
            `idle` before it ever reaches the position branch at rest, and once a command is sent
            `unverifiedSinceCommand` (above) nulls `livePosition` again. D243 then took the glyph's
            red, leaving it able to say *verified reachable this launch* or nothing. **So by D296
            the probe's every product was either dead or half-dead, and what it still cost was
            D125's lease on the gate root, taken on every view of this screen.** The role subtitle
            (kept after A11's own B1-informed pass) is gone too, per direct product feedback on the
            recomposed row — the name now centers vertically against the icon on its own.

            **The whole row is the entry to Personalize now, not just the icon** — per the same
            feedback pass, a narrow tap target on a row this prominent read as an accident
            waiting to happen. `.icon`/`.gate-identity-name` are non-interactive `span`s (the
            interactive element is the row itself); the pencil badge stays as the affordance
            that says the row does something.
          */}
          <button
            type="button"
            className="gate-identity"
            onClick={() => navigate.navigate({ id: 'personalize', gateId })}
            aria-label={t('personalizeIconLabel')}
          >
            {/* Fix D: same personalization approach as `HomeScreen`'s `GateCard` — the type-based
                `icon-{barrier,door,parking,garage}` class supplies the default tint, an
                overridden `color` swaps it via inline style, and the icon glyph itself falls
                back from a stored `VIEW_ICONS` value to the type default. */}
            <span
              className={`icon icon-${gate.type === 'garage' ? 'garage' : gate.type === 'parking' ? 'parking' : gate.type === 'door' ? 'door' : 'barrier'}`}
              style={
                color !== undefined && isViewColor(color)
                  ? { background: personalizationWash(color), color: personalizationColor(color) }
                  : undefined
              }
            >
              <Icon
                name={
                  icon !== undefined ? (renderNameForViewIcon(icon) as IconName) : iconForGateType(gate.type)
                }
                size={28}
              />
              <span className="gate-identity-icon-edit" aria-hidden="true">
                <Icon name="pencil" size={12} />
              </span>
            </span>
            <span className="gate-identity-name">
              <h1>{name}</h1>
            </span>
            {/*
              **The transport glyphs — both of them, which is D189(a) in one line of markup.**

              The old single glyph *swapped* wifi for bluetooth, which said the local path had
              replaced the cloud one — and under the withdrawn `reason: 'faster'` branch that was
              literally true. Now `wifi` is unconditional, because cloud is the primary path
              whenever there is a network at all, and `bluetooth` joins it whenever a local path
              exists: solid when a credential is in hand and a fallback could run at once, dimmed
              when it is offerable but cold.

              **D243 gave the wifi glyph the LED's one honest claim; D296 took the probe that was
              the only thing able to make it, so the glyph is unconditional and unmarked.** There
              is no green on this row at all now, and `is-live` is gone from the stylesheet with
              its last consumer rather than being left as a class nothing can set. The wifi glyph
              itself stays for D189(a)'s own reason, which never rested on the green: a lone
              bluetooth glyph is what a transport that has *replaced* the cloud one looks like, and
              that is the design D189(a) reversed. The bluetooth glyph still takes no green either
              — a warm credential proves the *server* handed us a password and says nothing about
              whether the controller is in range, so greening it would be D86's forbidden presence
              inference wearing D135's forbidden unwitnessed claim. `is-cold` stays exactly what it
              was: not a state, a statement about what a tap would cost.

              `aria-hidden`, because the transport *line* below says the same thing in words —
              two decorative glyphs in the a11y tree would read as controls the pair are not. The
              dot D243 removed was `aria-hidden` too and carried no label, so the a11y tree is
              unchanged by the move rather than diminished by it.
            */}
            <span className="gate-identity-transports">
              <Icon name="wifi" size={18} className="gate-identity-transport" aria-hidden={true} />
              {plan.primary === 'ble' || plan.fallback === 'ble' ? (
                <Icon
                  name="bluetooth"
                  size={18}
                  className={`gate-identity-transport${plan.primary === 'ble' || plan.fallbackReady ? '' : ' is-cold'}`}
                  aria-hidden={true}
                />
              ) : null}
            </span>
          </button>
          {offline ? <div className="gate-block">{t('offlineNotice')}</div> : null}
          {/*
            §6's calm line — *"Opens via Cloud"* / *"Bluetooth in range — faster"*.
            **Never a choice**: it states what will happen, and there is no control beside it.
            `transportLineKey` returns `null` on a device with no BLE at all, where naming the one
            transport that exists is noise rather than information.
          */}
          {transportKey !== null && !transportKey.startsWith('refusal.') ? (
            <p
              className={`gate-block gate-transport-line${transportKey === 'transportBleOffline' ? ' warning' : ''}`}
            >
              {tBle(transportKey as 'transportCloud' | 'transportCloudBleBackup' | 'transportBleOffline')}
            </p>
          ) : null}
          {/*
            **The refusal that must not misname its cause.** Past D61's window the copy says local
            access needs reconnection or service (PEC-25's existing offline voice) — never "gate
            not found", and never a silent failure, *"because a refusal that misnames the cause
            sends a resident to support with the wrong problem."* A device that has never synced
            gets its own sentence: there is no window to be past, and telling a new resident that
            something lapsed would be false.
          */}
          {plan.primary === 'none' ? (
            <div className="gate-block">{tBle(REFUSAL_KEY[plan.reason])}</div>
          ) : null}
          {/*
            **D272 F14 — the held tap says why it is held (D291 moved the subject from the button
            to the tap).**

            Since R5/D265 this screen paints from the snapshot; since D291 a tap made before
            `GET /gates/{gateId}` answers **or fails** is accepted and held, and goes out on the
            read's `finally`. On a hanging network — a lift, a basement with one bar — that is up to
            `REQUEST_TIMEOUT_MS`, so without this the resident watched a `sending` button and an
            overlay with **no sentence at all** saying what they were waiting on. This is the same
            class as `latchedManageHere` one line down — a state the screen already knows.

            **It claims only what is true**: the read is in flight, and nothing about the gate.
            It is withheld where something else already explains the control — a refusal — and it
            never appears without a held tap, because a resident merely looking at the screen
            across a slow read is waiting on nothing.

            **And it is delayed, which is D265's rule rather than a hedge.** `readConfirmed` is
            false on *every* entry to this screen and most taps land after it, so an immediate
            sentence would flash on every early tap — `useDelayedIndicator`'s own subject. The
            delay is on **appearing only**: the moment the read settles the tap dispatches and the
            sentence goes, so a fast read never shows it and a hanging one is explained.

            **The threshold is this screen's own (`HOLDING_FOR_ACCESS_DELAY_MS`) and not the list
            screens' `INDICATOR_DELAY_MS`, because D272 shipped on theirs and the flash came
            back.** At 200 ms an ordinary gate read — around half a second — crossed the threshold
            and then settled, so the sentence appeared and vanished on every entry: the delay was
            long enough to notice and too short to mean anything. See that constant for why a
            single gate read is a different distribution from a list screen's skeleton.
          */}
          {holdingForAccess && queued !== undefined && access.reason === null ? (
            <div className="gate-block">{t('openHoldingForAccess')}</div>
          ) : null}
          {/*
            **D299 removed the one term in here that was not a fault.** D297 put `alreadyOpening` —
            a Success — into this block because one `409 gate-busy` covered both directions and
            *sent* could therefore have been a claim about a command travelling the other way. **D294
            narrowed the code to an open holder**, so the Success is true as it stands and the block
            is back to holding only things that are wrong. D294's own refusal, `gate-closing`, is
            Failed and reaches it through `command.phase === 'failed'` like every other cause.
          */}
          {access.reason !== null || access.latched || command.phase === 'failed' ? (
            <div className="gate-block">
              <AccessBlockLabel
                reason={command.reason ?? (access.latched ? 'gate-latched' : access.reason)}
              />
              {/*
                **D265 §4.2 — the banner says *what*, and after the move it has to say *where*.**

                The label above names the fault; the control that clears it used to be visible
                below it on this screen and is now one page away. Skipping this is what would
                actually strand a manager who has locked themselves out, so it ships with the move
                rather than after it.

                **Named, not linked.** A status block that is sometimes a button is a second
                meaning for one treatment, and D144's split-by-what-it-is rule already governs that
                family — every other `.gate-block` here is inert. One string in five locales buys
                the route back; making this one interactive would buy it a second affordance
                nothing else on the screen has.

                **A separate span in P7's own namespace, never an edit to `AccessBlockLabel`.**
                That component and its `access` catalog are P6's, and Home renders the identical
                `gate-latched` label on a tile a *resident* sees — where naming a manager-only page
                would be an instruction they cannot follow.

                Manager-only, and gated on the latch rather than on `blockReason`: a subscription
                refusal or an expired grant is not something the Access page fixes.
              */}
              {manager && access.latched ? <span>{t('latchedManageHere')}</span> : null}
              {command.genericFailure ? (
                <span>{t(command.noData === true ? 'openNoData' : 'openFailed')}</span>
              ) : null}
              {/*
                R1-7(d): the named access refusal, never the generic message. **Its two `409`
                neighbours left with R4/D223**, which settles `gate-busy` and `open-in-flight`
                into `sent` rather than into a sentence of their own.
              */}
              {command.notAuthorized ? <span>{t('openNotAuthorized')}</span> : null}
              {/*
                PEC-8: a transport that could not confirm is not a gate that did not open — **and
                it is printed only where nothing else says so.** With an offer standing, the open
                button itself has become the second attempt's invitation and `fallbackAfterUnconfirmed`
                below states the same uncertainty in the words that matter to the decision; a
                device with no local path keeps this sentence, since there it is the whole report.
              */}
              {command.unconfirmed && fallbackOffer === undefined ? (
                // D272/D274 — the same slot, now naming the cause when the server named one. **D294
                // routes its refusal through here too** (`openFailureKey`, not `deliveryFailureKey`):
                // a close holding the lease is a reason rather than a `DeliveryOutcome`, and it earns
                // a sentence of its own because neither existing cause is true of it.
                <span>
                  {t(
                    openFailureKey({
                      refusal: command.openRefusal,
                      failure: command.deliveryFailure,
                    }),
                  )}
                </span>
              ) : null}
              {/*
                The local failures, each its own sentence. `timeout` reuses PEC-8's wording
                deliberately — a BLE write that got no `STA:` back is the same uncertainty as a
                cloud command that got no acknowledgement, and it is **not** proof the gate did
                nothing. `auth-failed` reuses PEC-25's reconnection-or-service voice rather than
                inventing a second failure vocabulary, exactly as D84 requires.
              */}
              {command.bleFailure !== undefined ? (
                <span>{tBle(bleFailureKey(command.bleFailure, command.cloudVerdict))}</span>
              ) : null}
              {/*
                **R7/D277 — the Failed close's own sentence, and it is not `unconfirmed`'s.**
                *The gate did not open* is not what a resident who asked for a close needs to
                read, so `closeFailureKey` answers this slot: `gate-busy` names *someone is opening
                the gate right now* (D223's Success row inverted), `close-not-supported` names a
                mode that changed under the screen, and each of D274's four causes has a close-voice
                sentence in `openFailure.ts`. An absent cause is *the server did not say* and earns
                today's generic close line.
              */}
              {command.closeFailed === true ? (
                <span>
                  {t(
                    closeFailureKey({
                      refusal: command.closeRefusal,
                      failure: command.closeFailure,
                    }),
                  )}
                </span>
              ) : null}
              {/*
                **D189(b)'s warning, now without D189(b)'s button.** The offer is answered by the
                command's own action below — pressing *Open* again is what dispatches locally —
                so what is left here is the one thing the resident has to read before pressing it:
                an ambiguous failure means the gate may already have the command, and that tap is
                knowingly a second one. A never-dispatched failure that could not auto-fall-back —
                a cold credential, or a spent activation — carries no warning, there being nothing
                to warn about.
              */}
              {fallbackOffer?.verdict === 'ambiguous' ? (
                <span>{tBle('fallbackAfterUnconfirmed')}</span>
              ) : null}
            </div>
          ) : null}
          {/*
            **R7/D283 — the card has TWO grammars, chosen by the gate's own close mode, and that
            is a reversal of D281 (a) rather than a gap in it.** D281 headed both kinds so the
            product had one grammar, at the stated cost of repainting a gate that gained nothing;
            D283 spends the other side of that trade instead — **an auto-close gate's actuators are
            byte-for-byte the control D120 shipped**, and only a gate that actually earned a Close
            gets the paired, headed card D281 designed for it.

            **The fork is `hasClose` and nothing else.** It reads `closeModeOf`, so the two
            grammars can be chosen only by the mode the installer set — never by a role, a layout,
            or a feature flag. What D281 (a) warned against is a screen whose grammar varies for
            reasons a resident cannot see; here the reason is the one visible difference between
            the two gates, which is that one of them has a second button.
          */}
          {hasClose ? (
            <>
              {/*
              **R7/D281 — two headed groups on EVERY gate, and heading only the close-on-tap one is
              the outcome this refuses.** The screen has one grammar: an auto-close gate renders one
              half under each heading and a close-on-tap gate renders two. What moved up into the
              headings is the subject each label used to say inside itself (`פתח שער`,
              `פתיחה להולכי רגל`) — which is why pairing the actuators is what retired those three
              strings rather than a copy preference.

              **Neither heading mints a glyph.** The gate's is `door-open`, the same Lucide mark the
              Gate details panel below heads itself with for the same subject; the pedestrian's is the
              filled Material walking figure this very control has carried since D120, moved off the
              button by D280's *one action, one glyph* rather than replaced. `user` was available and
              is refused: D245 spends it on *a manager* one screen over, and a second meaning for one
              glyph is a collision whichever surface reaches for it second.
            */}
              <div className="gate-command-group">
                <h2 className="gate-group-title">
                  <Icon name="door-open" size={16} /> {t('groupGate')}
                </h2>
                <div className="gate-group-actions pair">
                  <button
                    className={`gate-open-action${actionClass('normal')}${openOffer !== undefined ? ' offering' : ''}`}
                    type="button"
                    aria-busy={command.command === 'normal' && command.phase === 'sending'}
                    disabled={openOffer !== undefined ? offerDisabled : commandDisabled}
                    onClick={() =>
                      openOffer !== undefined
                        ? // R6 A3 — the resident's own second gesture is its own tap: the wait it
                          // measures starts here, not at the tap that failed.
                          void dispatchLocal(
                            openOffer.pedestrian,
                            openOffer.command,
                            openOffer.verdict,
                            beginOpenTiming({ surface: 'gate-detail', gateId: gate!.gateId }),
                          )
                        : void runCommand(false)
                    }
                  >
                    {openOffer !== undefined ? (
                      <>
                        <Icon name="bluetooth" size={20} /> <span>{tBle('tapAgainForBluetooth')}</span>
                      </>
                    ) : (
                      <>
                        {/* D280 — `key-round` is *open* on all four halves and on Home's plate. */}
                        <Icon name="key-round" size={20} /> <span>{openLabel}</span>
                      </>
                    )}
                  </button>
                  {hasClose ? (
                    <button
                      className={`gate-close-action${actionClass('close')}`}
                      type="button"
                      aria-busy={command.command === 'close' && command.phase === 'sending'}
                      disabled={closeDisabled}
                      onClick={() => void runClose(false)}
                    >
                      {/* D280 — `lock` is *close*. No offer branch: there is no BLE close (D277 (g)),
                        so this half can never be standing under a fallback. */}
                      <Icon name="lock" size={20} /> <span>{closeLabel}</span>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="gate-command-group">
                <h2 className="gate-group-title">
                  <MaterialIcon name="directions-walk" size={16} /> {t('groupPedestrian')}{' '}
                  <small>{t('groupPedestrianSub')}</small>
                </h2>
                <div className="gate-group-actions pair">
                  <button
                    className={`gate-pedestrian-action${actionClass('pedestrian')}${pedestrianOffer !== undefined ? ' offering' : ''}`}
                    type="button"
                    aria-busy={command.command === 'pedestrian' && command.phase === 'sending'}
                    disabled={pedestrianOffer !== undefined ? offerDisabled : commandDisabled}
                    onClick={() =>
                      pedestrianOffer !== undefined
                        ? void dispatchLocal(
                            pedestrianOffer.pedestrian,
                            pedestrianOffer.command,
                            pedestrianOffer.verdict,
                            beginOpenTiming({ surface: 'gate-detail', gateId: gate!.gateId }),
                          )
                        : void runCommand(true)
                    }
                  >
                    {pedestrianOffer !== undefined ? (
                      <>
                        <Icon name="bluetooth" size={20} /> <span>{tBle('tapAgainForBluetooth')}</span>
                      </>
                    ) : (
                      <>
                        <Icon name="key-round" size={20} /> <span>{pedestrianLabel}</span>
                      </>
                    )}
                  </button>
                  {hasClose ? (
                    <button
                      className={`gate-pedestrian-close-action${actionClass('pedestrian-close')}`}
                      type="button"
                      aria-busy={command.command === 'pedestrian-close' && command.phase === 'sending'}
                      disabled={closeDisabled}
                      onClick={() => void runClose(true)}
                    >
                      <Icon name="lock" size={20} /> <span>{pedestrianCloseLabel}</span>
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <>
              {/*
                **The pre-R7 pair, restored whole rather than approximated.** No heading, the
                `door-open` glyph D280 replaced on the paired card, `gate.openIdle` /
                `gate.pedestrianIdle` / `gate.pedestrianSub` saying their own subject, and the
                pedestrian half back on its outlined plate through `.solo` — which is the ONE
                declaration that separates the two treatments, `.gate-pedestrian-action` being
                shared so the pair's own rules cannot fork away from it unnoticed.
              */}
              <button
                className={`gate-open-action${actionClass('normal')}${openOffer !== undefined ? ' offering' : ''}`}
                type="button"
                aria-busy={command.command === 'normal' && command.phase === 'sending'}
                disabled={openOffer !== undefined ? offerDisabled : commandDisabled}
                onClick={() =>
                  openOffer !== undefined
                    ? // R6 A3 — the resident's own second gesture is its own tap: the wait it
                      // measures starts here, not at the tap that failed.
                      void dispatchLocal(
                        openOffer.pedestrian,
                        openOffer.command,
                        openOffer.verdict,
                        beginOpenTiming({ surface: 'gate-detail', gateId: gate!.gateId }),
                      )
                    : void runCommand(false)
                }
              >
                {openOffer !== undefined ? (
                  <>
                    <Icon name="bluetooth" size={20} /> <span>{tBle('tapAgainForBluetooth')}</span>
                  </>
                ) : (
                  <>
                    <Icon name="door-open" size={20} /> <span>{openLabel}</span>
                  </>
                )}
              </button>
              <button
                className={`gate-pedestrian-action solo${actionClass('pedestrian')}${pedestrianOffer !== undefined ? ' offering' : ''}`}
                type="button"
                aria-busy={command.command === 'pedestrian' && command.phase === 'sending'}
                disabled={pedestrianOffer !== undefined ? offerDisabled : commandDisabled}
                onClick={() =>
                  pedestrianOffer !== undefined
                    ? void dispatchLocal(
                        pedestrianOffer.pedestrian,
                        pedestrianOffer.command,
                        pedestrianOffer.verdict,
                        beginOpenTiming({ surface: 'gate-detail', gateId: gate!.gateId }),
                      )
                    : void runCommand(true)
                }
              >
                <span className="gate-pedestrian-copy">
                  <span className="gate-pedestrian-main">
                    {pedestrianOffer !== undefined ? (
                      <Icon name="bluetooth" size={20} />
                    ) : (
                      <MaterialIcon name="directions-walk" size={20} />
                    )}
                    <strong>
                      {pedestrianOffer !== undefined ? tBle('tapAgainForBluetooth') : pedestrianLabel}
                    </strong>
                  </span>
                  <small>{t('pedestrianSub')}</small>
                </span>
              </button>
            </>
          )}
        </section>

        {manager && gate.subscription !== undefined && expired ? (
          <div className={`renewal-strip expired`}>{t('expiredHint')}</div>
        ) : null}

        {/*
          A11 — the standalone Personalize panel (alias + icon/color rows) is gone; the identity
          icon above is the entry now (A11.3), so nothing here duplicates it.
        */}
        {/*
          **D265 — the panel is a page now, and one control below this one is what is left of it.**

          The access-list row, Activity, the subscription badge, לוחות זמנים and the gate-mode
          segmented control moved to `screens/access/AccessScreen.tsx` whole. What stayed behind was
          first a row *inside* this panel and is now the button after it, which is where the
          section's own shield-glyph heading came back — see that button's comment.
        */}
        {/* A11 — Gate details sits above the two exit buttons (it was above Access management,
            directly under the now-deleted Personalize panel). */}
        <section className="gate-panel">
          <h2 className="gate-panel-title">
            <Icon name="door-open" size={16} /> {t('detailsTitle')}
          </h2>
          {manager ? (
            <button
              className="gate-detail-row"
              type="button"
              disabled={offline}
              onClick={() => {
                setNameError(undefined)
                setEditingName(gate.defaultName)
              }}
            >
              <span className="gate-row-label">{t('officialNameLabel')}</span>
              <span className="gate-row-value">
                {gate.defaultName} <Icon name="chevron-right" size={14} />
              </span>
            </button>
          ) : (
            <DetailRow label={t('officialNameLabel')} value={gate.defaultName} />
          )}
          {/*
            **R7/D279 — the mode, read-only, and its two values are READ from `install` rather than
            copied into `gate`.** They are the same two strings the install step's segments and the
            update menu's pill carry (`install.closeAuto` / `install.closeOnTap`), and a second copy
            here would be two strings in five catalogs that agree until one is retranslated —
            D269's own rule against exactly that. It is a `DetailRow` with no `onClick`, so it
            renders a `<div>` and no chevron: this is a fact about the gate, not a destination, and
            the mode is changed from the installer's update menu (D267) and never from here.
          */}
          <DetailRow label={t('gateTypeLabel')} value={tInstall(hasClose ? 'closeOnTap' : 'closeAuto')} />
          <div className="gate-detail-row">
            <span className="gate-row-label">{t('gateIdLabel')}</span>
            <span className="gate-row-value logical-id-value">
              <bdi className="gate-logical-id" dir="ltr">
                {formatGateId(gateId)}
              </bdi>
            </span>
          </div>
        </section>

        {/*
          **Product feedback, 2026-09-08 — the entry is a control of its own, below the panel it
          used to sit inside.** As a `DetailRow` it read as one more fact about the gate, in a list
          whose other rows state a name and an id; it is the only thing on that panel that leaves
          the screen. It wears `.gate-panel-title`'s treatment — the `--g1` band, the accent glyph,
          the same type — because that is literally what it opens: the Access page's own heading is
          this string under this glyph (`AccessScreen.tsx`), so the button and its destination are
          one shape rather than two that agree.

          **The glyph is `shield`, deliberately, and §4.7's *`shield` is not available for this*
          does not reach here.** That rule refuses a second *meaning* for the glyph — D40 spends it
          on the latched operating state — and this is not a second meaning: it is the Access page's
          own heading glyph, on the button that opens it.

          Still `{manager ? … : null}`, exactly as the row and the panel before it were: a resident
          has never had this.
        */}
        {manager ? (
          <button
            className="gate-panel-entry"
            type="button"
            onClick={() => {
              // The handoff, deposited at the tap rather than at the read: the gate is whatever
              // this screen is currently rendering — the fresh read where it has landed, the
              // cached seed where it has not — and either is what the next screen would have
              // painted from anyway. The roster is deposited by its own read, above.
              putGateHandoff(gateId, { gate })
              navigate.navigate({ id: 'access', gateId })
            }}
          >
            <Icon name="shield" size={16} />
            <span className="gate-panel-entry-label">{t('accessManagementTitle')}</span>
            <Icon name="chevron-right" size={14} className="gate-panel-entry-chevron" />
          </button>
        ) : null}

        {/*
          **2026-08-14: the row itself is absent for a confirmed sole manager**, rather than a
          row that opens a popover only to answer with D143's refusal. The popover flashed
          between the confirmation and the alert on that path (`knownManagerCount` narrowed the
          window but could not close it, since the seed itself was fed by an async roster read);
          removing the affordance removes the swap along with it. The row is still the *default*
          — `soleManager` requires a *confirmed* `1`, so `unknown`/`failed` leave it in place,
          same fail-open rule `RemoveMePopover` already applies to itself.

          **D144: opens a popover, does not navigate** — which is why the pill it became carries
          no chevron: this app spends a chevron on a destination, and there is none here.

          **D153 closes the remaining flash**: `soleManager` above used to come from this
          screen's own roster read alone, so a cached sole manager still saw the row for one
          frame on mount, before that read resolved. It now reads `knownManagerCount`, seeded
          from `gate.managerCount` (the projection or the offline snapshot) and corrected by the
          roster read once it lands, so a cached sole manager renders no row at all, ever.

          **The refusal note is no longer this control's `else` branch** (product feedback,
          2026-09-08) — it renders below Delete gate instead, so the two are siblings rather than
          two arms of one ternary. `soleManager` still decides both and is still the only thing
          that does, which is what keeps the pair mutually exclusive: the note is the sentence
          explaining an absent button, and it may never appear beside a present one.
        */}
        {soleManager ? null : (
          <button className="gate-remove" type="button" onClick={() => setRemovingMe(true)}>
            <Icon name="log-out" size={15} /> {t('removeMeRow')}
          </button>
        )}

        {/* A standalone button of the same shape as Remove me above it, rather than a row inside
            Access management: the two are the ways *out* of this gate and belong together at the
            foot of the screen. Manager-only.

            **Product feedback, 2026-09-08 — both are the account-deletion pill's size now**, not
            full-width rows. A row spanning the screen with a chevron is what this app gives a
            destination; these open a confirmation, and at full width they were the two largest
            controls on a screen whose largest control should be *Open*. `.deletion-pill` already
            carries that argument for account erasure one screen over, so it is the shape reused
            rather than a third one minted, and the chevron goes with the row it belonged to.

            **It is LAST since D268, and that amends a written clause rather than ignoring one.**
            D120 enumerated "then Remove me as the final action" and D114 carried the same
            "final Remove-me placement"; D268 amends those two clauses and no others. **§13 carries
            no clause about the order of these two**, so the ordering case in this screen's suite is
            the only thing in the repository that would notice it reverting.

            What the order buys, beyond the request: on a sole-manager gate the paragraph that
            replaces Remove me ends "…או מחקו את השער", and the button it names now follows it
            instead of sitting above the sentence that points at it.

            **Neutral, not red.** Two red buttons stacked cancel each other out — whichever is
            more alarming makes the other look safe, and the more destructive of the pair would be
            the one de-emphasised by contrast. The octagon carries the weight instead, and the
            confirmation dialog behind it is where the warning actually lives — which is why the
            glyphs stay on a pill that has room for nothing else. */}
        {manager ? (
          <button
            className="gate-remove neutral"
            type="button"
            disabled={offline}
            onClick={() => {
              setDeleteError(false)
              setDeleting(true)
            }}
          >
            <Icon name="octagon-x" size={15} /> {t('deleteGateRow')}
          </button>
        ) : null}

        {/* **The sole-manager refusal, below the button it points at** (product feedback,
            2026-09-08). It is the sentence that stands in for Remove me, and its own last clause
            is "…או מחקו את השער" — so above Delete gate it named a control the reader had not
            reached yet, and below it the sentence and its escape hatch read in order. It is not a
            control and keeps its full width deliberately: shrinking prose to a pill would make an
            explanation look like a third thing to tap.

            The condition is `soleManager` and nothing else, so this and the Remove me button
            above are mutually exclusive by construction — see that block's own comment. */}
        {soleManager ? (
          <p
            className="gate-remove-blocked gate-remote-warning"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-4) var(--space-5)',
            }}
          >
            <Icon name="log-out" size={15} /> <span>{tRemoveMe('blockedBody')}</span>
          </p>
        ) : null}
      </main>

      {removingMe ? (
        <FrameOverlay>
          <RemoveMePopover
            gateId={gateId}
            {...(knownManagerCount === undefined ? {} : { knownManagerCount })}
            onClose={() => setRemovingMe(false)}
          />
        </FrameOverlay>
      ) : null}

      {editingName !== undefined ? (
        <FrameOverlay>
          <Popover
            title={t('editNameTitle')}
            body={t('editNameBody')}
            value={editingName}
            onChange={(next) => {
              setEditingName(next)
              setNameError(undefined)
            }}
            {...(nameError === undefined ? {} : { error: t(NAME_ERROR_KEYS[nameError]) })}
            busy={nameBusy}
            cancelLabel={tCommon('cancel')}
            confirmLabel={t('confirm')}
            onCancel={() => setEditingName(undefined)}
            onConfirm={() => void confirmName()}
          />
        </FrameOverlay>
      ) : null}

      {deleting ? (
        <FrameOverlay>
          <DeleteGateDialog
            gateName={name}
            busy={deleteInFlight}
            {...(deleteError ? { error: t('deleteGateFailed') } : {})}
            onCancel={() => setDeleting(false)}
            onConfirm={() => void confirmDelete()}
          />
        </FrameOverlay>
      ) : null}

      {/* Through `FrameOverlay` for the same reason as the popovers above, and declared after them
          so a wait raised over an open dialog is on top of it. No `key` is needed to reset the
          overlay's clock between opens: `runCommand` raises one only while `overlay` is
          `undefined`, so every open gets a fresh instance by construction. The toast host stays
          last — a toast is what reports the *screen's* other business, and it outranks the wait. */}
      {overlay === undefined ? null : (
        <FrameOverlay>
          <OpenAnimation
            style={overlay.style}
            outcome={overlay.outcome}
            onDone={() => setOverlay(undefined)}
          />
        </FrameOverlay>
      )}

      {/* The host renders nothing until something is in the store, so it needs no condition of
          its own — and it must be inside `FrameOverlay` for the same reason the popovers are:
          `.app-screen` is the scroll container an absolute offset would otherwise resolve
          against. */}
      <FrameOverlay>
        <ToastHost />
      </FrameOverlay>
    </>
  )
}
